import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Spinner,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  IconButton,
  Badge,
  useColorModeValue,
  Tooltip,
  HStack,
  Card,
  CardBody,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  Avatar,
  Grid,
  GridItem,
  Divider,
  Link,
  Tag,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Checkbox,
} from '@chakra-ui/react';
import './freelancerDetailPage.css';
import FdpSelect from './FdpSelect';
import FdpMultiSelect from './FdpMultiSelect';

import { 
  MdVisibility, 
  MdChevronLeft, 
  MdChevronRight,
  MdPerson,
  MdWork,
  MdFolder,
  MdSchool,
  MdCardMembership,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdBusiness,
  MdLanguage,
  MdAttachMoney,
  MdCalendarToday,
  MdLink,
  MdGetApp,
  MdPublic,
  MdDescription,
  MdNotifications,
  MdSchedule,
  MdInfo,
  MdEdit,
  MdAdd,
  MdDelete,
} from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { MdRefresh } from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import { statusChange } from '../../../features/admin/clientManagementSlice';
import { showError, showSuccess } from '../../../helpers/messageHelper';
import {
  freelancerData,
  freelancerCompleteData,
} from '../../../features/admin/freelancerManagementSlice';
import { postApi, getApi, putApi } from '../../../services/api';
import { config } from '../../../config/config';
import { Country, State, City } from 'country-state-city';
import { apiEndPoints } from '../../../config/path';
import { UserStatus } from 'utils/enums';

/** Title-case each word in first/last for display (e.g. "kunj VIRANI" → "Kunj Virani"). */
function formatFreelancerDisplayName(firstName, lastName) {
  const titleCaseWord = (word) => {
    const w = String(word ?? '').trim();
    if (!w) return '';
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  };
  const titleCaseSegment = (segment) =>
    String(segment ?? '')
      .trim()
      .split(/\s+/)
      .map(titleCaseWord)
      .filter(Boolean)
      .join(' ');
  return [titleCaseSegment(firstName), titleCaseSegment(lastName)].filter(Boolean).join(' ');
}

const FREELANCER_STATUS_FILTER_OPTIONS = [
  { label: 'Pending For Registration', value: 'incomplete' },
  { label: 'Pending For Approval', value: 'pending' },
  { label: 'On-Hold', value: 'onhold' },
];

function FreelancerList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [selectedFreelancer, setSelectedFreelancer] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const statusModal = useDisclosure();
  const reminderModal = useDisclosure();
  const reminderViewModal = useDisclosure();
  const emailModal = useDisclosure();
  const addProfileModal = useDisclosure();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusFilterValue, setStatusFilterValue] = useState('');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedFreelancerIds, setSelectedFreelancerIds] = useState([]);
  const [isBulkReminderMode, setIsBulkReminderMode] = useState(false);
  const [reminderData, setReminderData] = useState({
    first_reminder_in_days: '',
    second_reminder_in_days: '',
    third_reminder_in_days: '',
  });
  const [isReminderLoading, setIsReminderLoading] = useState(false);
  const [selectedReminderData, setSelectedReminderData] = useState(null);
  const statusOptions = ['Pending', 'Approved', 'Rejected', 'Under Review', 'Suspended'];

  // Add Profile modal state
  const [profileCategory, setProfileCategory] = useState('');
  const [profileSlugId, setProfileSlugId] = useState('');
  const [websiteCategories, setWebsiteCategories] = useState([]);
  const [websiteSlugsByCategory, setWebsiteSlugsByCategory] = useState([]);
  const [isProfileModalLoading, setIsProfileModalLoading] = useState(false);
  const [isSlugLoading, setIsSlugLoading] = useState(false);

  // Determine which page variant is active based on path
  // Registration -> Freelancers is at /freelancers
  // Management -> Freelancers is at /freelancers-management
  const isManagementPage = location.pathname === '/admin/freelancers-management';

  // Set filters:
  // - Management page shows only approved
  // - Registration (Freelancers) shows all others (exclude approved)
  const statusFilter = statusFilterValue || (isManagementPage ? UserStatus.APPROVED : null);
  const excludeStatusFilter = statusFilterValue ? null : (!isManagementPage ? UserStatus.APPROVED : null);

  const {
    isLoading,
    responseData,
  } = useSelector((s) => s.freelancerDataReducer || {});

  const rows = useMemo(() => {
    const list = responseData?.freelancers;
    const arr = Array.isArray(list) ? list : [];
    const total = typeof responseData?.total_count === 'number' ? responseData.total_count : arr.length;
    setTotalCount(total);
    return arr;
  }, [responseData]);

  const currentPageFreelancerIds = useMemo(
    () => rows.map((fr) => fr?.id).filter(Boolean),
    [rows],
  );
  const isAllCurrentPageSelected = useMemo(
    () => currentPageFreelancerIds.length > 0
      && currentPageFreelancerIds.every((id) => selectedFreelancerIds.includes(id)),
    [currentPageFreelancerIds, selectedFreelancerIds],
  );

  useEffect(() => {
    dispatch(freelancerData({
      page,
      pageLimit,
      status: statusFilter,
      excludeStatus: excludeStatusFilter,
    }));
  }, [dispatch, page, pageLimit, statusFilter, excludeStatusFilter]);

  useEffect(() => {
    setSelectedFreelancerIds([]);
  }, [page, pageLimit, statusFilterValue, isManagementPage]);

  useEffect(() => {
    if (!isSelectionMode) {
      setSelectedFreelancerIds([]);
    }
  }, [isSelectionMode]);

  useEffect(() => {
    if (isManagementPage) {
      setIsSelectionMode(false);
      setSelectedFreelancerIds([]);
    }
  }, [isManagementPage]);

  const openDetails = (userId) => {
    navigate(`/admin/freelancer/${userId}`, { state: { from: location.pathname } });
  };

  const openEditDetails = (userId) => {
    navigate(`/admin/freelancer/${userId}?edit=1`, { state: { from: location.pathname } });
  };

  const openStatus = (freelancer) => {
    setSelectedFreelancer(freelancer);
    setSelectedStatus(freelancer?.status || 'Pending');
    statusModal.onOpen();
  };

  const closeStatus = () => {
    statusModal.onClose();
    setSelectedFreelancer(null);
    setSelectedStatus('');
  };

  const applyStatusChange = async () => {
    if (!selectedFreelancer) return;
    try {
      await dispatch(statusChange({ id: selectedFreelancer.id, apiData: { status: selectedStatus } }));
      showSuccess('Status updated successfully');
      // refetch with current filters
      dispatch(freelancerData({
        page,
        pageLimit,
        status: statusFilter,
        excludeStatus: excludeStatusFilter,
      }));
    } catch (e) {
      showError('Failed to update status');
    } finally {
      closeStatus();
    }
  };

  const openReminder = (freelancer) => {
    setSelectedFreelancer(freelancer);
    setIsBulkReminderMode(false);
    setReminderData({
      first_reminder_in_days: '',
      second_reminder_in_days: '',
      third_reminder_in_days: '',
    });
    reminderModal.onOpen();
  };

  const closeReminder = () => {
    reminderModal.onClose();
    setSelectedFreelancer(null);
    setIsBulkReminderMode(false);
    setReminderData({
      first_reminder_in_days: '',
      second_reminder_in_days: '',
      third_reminder_in_days: '',
    });
  };

  const openBulkReminder = () => {
    if (selectedFreelancerIds.length === 0) {
      showError('Please select at least one freelancer');
      return;
    }
    setSelectedFreelancer(null);
    setIsBulkReminderMode(true);
    setReminderData({
      first_reminder_in_days: '',
      second_reminder_in_days: '',
      third_reminder_in_days: '',
    });
    reminderModal.onOpen();
  };

  const handleSelectAllCurrentPage = (checked) => {
    if (checked) {
      setSelectedFreelancerIds((prev) => [...new Set([...prev, ...currentPageFreelancerIds])]);
    } else {
      setSelectedFreelancerIds((prev) => prev.filter((id) => !currentPageFreelancerIds.includes(id)));
    }
  };

  const toggleFreelancerSelection = (freelancerId) => {
    setSelectedFreelancerIds((prev) => (
      prev.includes(freelancerId)
        ? prev.filter((id) => id !== freelancerId)
        : [...prev, freelancerId]
    ));
  };

  const handleReminderSubmit = async () => {
    if (!selectedFreelancer && !isBulkReminderMode) return;

    // Validate that at least one reminder day is provided
    if (!reminderData.first_reminder_in_days && !reminderData.second_reminder_in_days && !reminderData.third_reminder_in_days) {
      showError('Please provide at least one reminder day');
      return;
    }

    setIsReminderLoading(true);
    try {
      const baseData = {
        first_reminder_in_days: reminderData.first_reminder_in_days || null,
        second_reminder_in_days: reminderData.second_reminder_in_days || null,
        third_reminder_in_days: reminderData.third_reminder_in_days || null,
      };

      if (isBulkReminderMode) {
        await postApi(`${config.apiBaseUrl}/admin/profile-complete-reminder/bulk`, {
          userIds: selectedFreelancerIds,
          ...baseData,
        });
        showSuccess('Profile complete reminder set for selected freelancers');
        setSelectedFreelancerIds([]);
        setIsSelectionMode(false);
      } else {
        await postApi(`${config.apiBaseUrl}/admin/profile-complete-reminder`, {
          userId: selectedFreelancer.id,
          ...baseData,
        });
        showSuccess('Profile complete reminder set successfully');
      }
      closeReminder();
      // Refetch freelancer data to update the list
      dispatch(freelancerData({
        page,
        pageLimit,
        status: statusFilter,
        excludeStatus: excludeStatusFilter,
      }));
    } catch (error) {
      console.error('Error setting reminder:', error);
      showError(error?.response?.data?.message || 'Failed to set reminder');
    } finally {
      setIsReminderLoading(false);
    }
  };

  const openReminderView = (freelancer) => {
    setSelectedFreelancer(freelancer);
    setSelectedReminderData(freelancer.profile_complete_reminder || null);
    reminderViewModal.onOpen();
  };

  const openEmailModal = (freelancer) => {
    setSelectedFreelancer(freelancer);
    emailModal.onOpen();
  };

  const closeReminderView = () => {
    reminderViewModal.onClose();
    setSelectedFreelancer(null);
    setSelectedReminderData(null);
  };

  // Add Profile modal handlers
  const fetchWebsiteCategories = async () => {
    if (websiteCategories.length > 0) {
      return;
    }

    setIsProfileModalLoading(true);
    try {
      const response = await getApi(apiEndPoints.GET_WEBSITE_CATEGORIES);
      const data = response?.data?.data || [];
      setWebsiteCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('fetchWebsiteCategories error:', error);
      showError(error?.response?.data?.message || 'Failed to fetch website categories');
    } finally {
      setIsProfileModalLoading(false);
    }
  };

  const fetchSlugsForCategory = async (categoryName) => {
    if (!categoryName) {
      setWebsiteSlugsByCategory([]);
      return;
    }

    setIsSlugLoading(true);
    try {
      const response = await getApi(`${apiEndPoints.GET_WEBSITE_DATA_BY_CATEGORY_ADMIN}/${encodeURIComponent(categoryName)}`);
      const data = response?.data?.data || [];
      setWebsiteSlugsByCategory(Array.isArray(data) ? data : []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('fetchSlugsForCategory error:', error);
      showError(error?.response?.data?.message || 'Failed to fetch website slugs for selected category');
      setWebsiteSlugsByCategory([]);
    } finally {
      setIsSlugLoading(false);
    }
  };

  const openAddProfile = async (freelancer) => {
    setSelectedFreelancer(freelancer);
    setProfileCategory('');
    setProfileSlugId('');
    setWebsiteSlugsByCategory([]);
    addProfileModal.onOpen();
    await fetchWebsiteCategories();
  };

  const closeAddProfile = () => {
    addProfileModal.onClose();
    setSelectedFreelancer(null);
    setProfileCategory('');
    setProfileSlugId('');
    setWebsiteSlugsByCategory([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      });
    } catch (e) {
      return '--';
    }
  };

  // Chakra color mode values (match ClientList look)
  const textColor = useColorModeValue('rgb(32, 33, 36)', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgColor = useColorModeValue('#FFFFFF', 'black');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const getStatusColorScheme = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case UserStatus.INCOMPLETE:
        return { bg: 'transparent', color: 'black', border: 'black.600' };
      case UserStatus.APPROVED:
      case 'otpverified':
        return { bg: 'transparent', color: 'black', border: 'black.600' };
      case 'rejected':
        return { bg: 'transparent', color: 'black', border: 'black.600' };
      case 'under review':
      case 'incomplete':
      case UserStatus.PENDING:
      case 'completed':
      case 'complete':
        return { bg: 'transparent', color: 'black', border: 'black.600' };
      default:
        return { bg: 'transparent', color: 'black', border: 'black.600' };
    }
  };

  const totalPages = Math.ceil(totalCount / pageLimit) || 1;

  return (
    <Box>
      <Card bg={bgColor}>
        <Box p="12px">
          <Flex justify="space-between" align={{ base: 'start', md: 'center' }} gap={3} mb="10px" flexWrap="wrap">
            <Text color={textColor} fontSize="l" fontWeight="700" mb="0">
              Freelancers
            </Text>

            {!isManagementPage && (
              <HStack spacing="10px" align="center">
                <Text color="black" fontSize="sm" fontWeight="600" whiteSpace="nowrap">
                  Status:
                </Text>
                <Select
                  size="sm"
                  w={{ base: '220px', md: '260px' }}
                  value={statusFilterValue}
                  onChange={(e) => {
                    setStatusFilterValue(e.target.value);
                    setPage(1);
                  }}
                  borderColor={borderColor}
                  _hover={{ borderColor: 'brand.500' }}
                >
                  <option value="">All</option>
                  {FREELANCER_STATUS_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  colorScheme="brand"
                  leftIcon={<MdNotifications />}
                  onClick={openBulkReminder}
                  isDisabled={!isSelectionMode || selectedFreelancerIds.length === 0}
                  whiteSpace="nowrap"
                >
                  Set Reminder
                </Button>
                {isSelectionMode && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsSelectionMode(false)}
                    whiteSpace="nowrap"
                  >
                    Done
                  </Button>
                )}
              </HStack>
            )}
          </Flex>

          {isLoading && rows.length === 0 ? (
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="brand.500" />
            </Flex>
          ) : (
            <>
              <Box
                h={{ base: 'calc(100vh - 160px)', md: 'calc(100vh - 130px)', xl: 'calc(100vh - 130px)' }}
                overflowY="auto"
                overflowX="auto"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="8px"
              >
                <Table variant="simple" color="gray.500" minW="1000px">
                  <Thead position="sticky" top="0" zIndex="1" bg={bgColor}>
                    <Tr>
                      {!isManagementPage && (
                        <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" textAlign="center" bg={bgColor} w="110px">
                          {!isSelectionMode ? (
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setIsSelectionMode(true)}
                              color="brand.500"
                              fontWeight="700"
                            >
                              Select
                            </Button>
                          ) : (
                            <HStack spacing={2} justify="center">
                              <Checkbox
                                isChecked={isAllCurrentPageSelected}
                                onChange={(e) => handleSelectAllCurrentPage(e.target.checked)}
                                colorScheme="brand"
                              />
                              <Text fontSize="xs" fontWeight="600" color="black" whiteSpace="nowrap">
                                Select all
                              </Text>
                            </HStack>
                          )}
                        </Th>
                      )}
                      <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" bg={bgColor}>
                        First Name
                      </Th>
                      <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" bg={bgColor}>
                        Last Name
                      </Th>
                      <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" bg={bgColor}>
                        Sub Categories
                      </Th>
                      <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" bg={bgColor}>
                        Location
                      </Th>
                      <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" bg={bgColor}>
                        Last Login
                      </Th>
                      <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" textAlign="center" bg={bgColor}>
                        Status
                      </Th>
                      <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" textAlign="center" bg={bgColor}>
                        View
                      </Th>
                      {isManagementPage && (
                        <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" textAlign="center" bg={bgColor}>
                          Add Profile
                        </Th>
                      )}
                      {!isManagementPage && (
                        <Th borderColor={borderColor} color="black" fontSize="xs" fontWeight="700" textTransform="capitalize" textAlign="center" bg={bgColor}>
                          Reminder
                        </Th>
                      )}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.length === 0 ? (
                      <Tr>
                        <Td colSpan={isManagementPage ? 8 : 9} textAlign="center" py="40px">
                          <Text color="black">No freelancers found</Text>
                        </Td>
                      </Tr>
                    ) : (
                      rows.map((fr, index) => {
                        // Format sub categories
                        const subCategories = Array.isArray(fr.sub_category_names) && fr.sub_category_names.length > 0
                          ? fr.sub_category_names.join(', ')
                          : '--';

                        // Format location
                        const location = [fr.city, fr.country].filter(Boolean).join(' - ') || '--';

                        // Format last login
                        const lastLoginDate = fr.freelancer_last_login || fr.last_login;
                        let lastLogin = '--';
                        if (lastLoginDate) {
                          try {
                            const date = new Date(lastLoginDate);
                            lastLogin = date.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                          } catch (e) {
                            lastLogin = '--';
                          }
                        }

                        const statusColors = getStatusColorScheme(fr.status);
                        // Apply background color to odd rows (1st, 3rd, 5th, etc.)
                        const isOddRow = index % 2 === 0;

                        return (
                          <Tr key={fr.id} bg={isOddRow ? '#F4F7FE' : 'transparent'} _hover={{ bg: hoverBg }} transition="all 0.2s">
                            {!isManagementPage && (
                              <Td borderColor={borderColor} textAlign="center" pt="8px" pb="8px">
                                {isSelectionMode && (
                                  <Checkbox
                                    isChecked={selectedFreelancerIds.includes(fr.id)}
                                    onChange={() => toggleFreelancerSelection(fr.id)}
                                    colorScheme="brand"
                                  />
                                )}
                              </Td>
                            )}
                            <Td borderColor={borderColor} pt="8px" pb="8px">
                              <Text color={textColor} fontSize="sm" fontWeight="normal">{fr.first_name || '--'}</Text>
                            </Td>
                            <Td borderColor={borderColor} pt="8px" pb="8px">
                              <Text color={textColor} fontSize="sm" fontWeight="normal">{fr.last_name || '--'}</Text>
                            </Td>
                            <Td borderColor={borderColor} pt="8px" pb="8px">
                              <Text color={textColor} fontSize="sm" fontWeight="normal">{subCategories}</Text>
                            </Td>
                            <Td borderColor={borderColor} pt="8px" pb="8px">
                              <Text color={textColor} fontSize="sm" fontWeight="normal">{location}</Text>
                            </Td>
                            <Td borderColor={borderColor} pt="8px" pb="8px">
                              <Text color={textColor} fontSize="sm" fontWeight="normal">{lastLogin}</Text>
                            </Td>
                            <Td borderColor={borderColor} textAlign="center" pt="8px" pb="8px">
                              <Button
                                size="sm"
                                bg={statusColors.bg}
                                color={statusColors.color}
                                borderColor={statusColors.border}
                                borderWidth="2px"
                                borderRadius="full"
                                fontWeight="normal"
                                fontSize="xs"
                                textTransform="capitalize"
                                _hover={{ opacity: 0.8, transform: 'translateY(-2px)' }}
                                onClick={() => openStatus(fr)}
                              >
                                {fr.status || 'Pending'}
                              </Button>
                            </Td>
                            <Td borderColor={borderColor} textAlign="center" pt="8px" pb="8px">
                              <HStack spacing={2} justify="center">
                                <Tooltip label="View Freelancer Details">
                                  <IconButton
                                    aria-label="View freelancer"
                                    icon={<MdVisibility />}
                                    size="sm"
                                    variant="ghost"
                                    style={{ color: 'rgb(32, 33, 36)' }}
                                    onClick={() => openDetails(fr.id)}
                                  />
                                </Tooltip>
                                <Tooltip label="Edit Freelancer Details">
                                  <IconButton
                                    aria-label="Edit freelancer"
                                    icon={<MdEdit />}
                                    size="sm"
                                    variant="ghost"
                                    style={{ color: 'rgb(32, 33, 36)' }}
                                    onClick={() => openEditDetails(fr.id)}
                                  />
                                </Tooltip>
                                <Tooltip label="Send Email to Freelancer">
                                  <IconButton
                                    aria-label="Send email"
                                    icon={<MdEmail />}
                                    size="sm"
                                    variant="ghost"
                                    style={{ color: 'rgb(32, 33, 36)' }}
                                    onClick={() => openEmailModal(fr)}
                                  />
                                </Tooltip>
                              </HStack>
                            </Td>
                            {isManagementPage && (
                              <Td borderColor={borderColor} textAlign="center" pt="8px" pb="8px">
                                <Button
                                  size="sm"
                                  leftIcon={<MdAdd />}
                                  variant="outline"
                                  colorScheme="brand"
                                  onClick={() => openAddProfile(fr)}
                                >
                                  Add Profile
                                </Button>
                              </Td>
                            )}
                            {!isManagementPage && (
                              <Td borderColor={borderColor} textAlign="center" pt="8px" pb="8px">
                                  <HStack spacing={2} justify="center">
                                    <Tooltip label="Set Profile Complete Reminder">
                                      <IconButton
                                        aria-label="Set reminder"
                                        icon={<MdNotifications />}
                                        size="sm"
                                        variant="ghost"
                                        style={{ color: 'rgb(32, 33, 36)' }}
                                        onClick={() => openReminder(fr)}
                                      />
                                    </Tooltip>
                                    {fr.profile_complete_reminder && (
                                      <Tooltip label="View Reminder Details">
                                        <IconButton
                                          aria-label="View reminder"
                                          icon={<MdInfo />}
                                          size="sm"
                                          variant="ghost"
                                          style={{ color: 'rgb(32, 33, 36)' }}
                                          onClick={() => openReminderView(fr)}
                                        />
                                      </Tooltip>
                                    )}
                                  </HStack>
                                </Td>
                            )}
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              <Flex justify="space-between" align="center" pt="8px" flexWrap="wrap" gap="8px">
                <HStack spacing="12px">
                  <Text color="black" fontSize="sm">
                    Showing <Text as="span" fontWeight="700" color="brand.500">{rows.length}</Text> of {totalCount}
                  </Text>
                  <HStack spacing="8px">
                    <Text color="black" fontSize="sm" whiteSpace="nowrap">Per page:</Text>
                    <Select
                      size="sm"
                      w="80px"
                      value={pageLimit}
                      onChange={(e) => {
                        setPageLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      borderColor={borderColor}
                      _hover={{ borderColor: 'brand.500' }}
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={300}>300</option>
                    </Select>
                  </HStack>
                </HStack>

                <HStack spacing="8px">
                  <IconButton aria-label="Previous page" icon={<MdChevronLeft />} size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page === 1} variant="outline" />
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .slice(0, 10)
                    .map((p) => (
                      <Button key={p} size="sm" variant={page === p ? 'solid' : 'outline'} colorScheme={page === p ? 'brand' : 'gray'} onClick={() => setPage(p)}>
                        {p}
                      </Button>
                    ))}
                  <IconButton aria-label="Next page" icon={<MdChevronRight />} size="sm" onClick={() => setPage((p) => p + 1)} isDisabled={page === totalPages} variant="outline" />
                </HStack>
              </Flex>
            </>
          )}
        </Box>
      </Card>

      {/* Status Change Modal */}
      <Modal isOpen={statusModal.isOpen} onClose={closeStatus} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Status</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {statusOptions.map((status) => (
              <Box key={status} mb="12px">
                <HStack>
                  <input
                    type="checkbox"
                    checked={selectedStatus === status}
                    onChange={() => setSelectedStatus(status)}
                    style={{ width: 16, height: 16 }}
                  />
                  <Text fontSize="sm" fontWeight="500">{status}</Text>
                </HStack>
              </Box>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeStatus}>Cancel</Button>
            <Button colorScheme="brand" onClick={applyStatusChange}>OK</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reminder View Modal */}
      <Modal isOpen={reminderViewModal.isOpen} onClose={closeReminderView} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Profile Complete Reminder Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedReminderData ? (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">
                    First Reminder Date
                  </Text>
                  <Text fontSize="md" color={textColor}>
                    {formatDate(selectedReminderData.first_reminder_date) || 'Not set'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">
                    Second Reminder Date
                  </Text>
                  <Text fontSize="md" color={textColor}>
                    {formatDate(selectedReminderData.second_reminder_date) || 'Not set'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">
                    Third Reminder Date
                  </Text>
                  <Text fontSize="md" color={textColor}>
                    {formatDate(selectedReminderData.third_reminder_date) || 'Not set'}
                  </Text>
                </Box>
              </VStack>
            ) : (
              <Text color="gray.500">No reminder data available</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={closeReminderView}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reminder Modal */}
      <Modal isOpen={reminderModal.isOpen} onClose={closeReminder} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isBulkReminderMode
              ? `Set Profile Complete Reminder (${selectedFreelancerIds.length} selected)`
              : 'Set Profile Complete Reminder'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500">
                  First Reminder (Days)
                </FormLabel>
                <NumberInput
                  value={reminderData.first_reminder_in_days || ''}
                  onChange={(valueString, valueNumber) =>
                    setReminderData({
                      ...reminderData,
                      first_reminder_in_days: isNaN(valueNumber) ? '' : valueNumber,
                    })
                  }
                  min={0}
                >
                  <NumberInputField placeholder="Enter days" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500">
                  Second Reminder (Days)
                </FormLabel>
                <NumberInput
                  value={reminderData.second_reminder_in_days || ''}
                  onChange={(valueString, valueNumber) =>
                    setReminderData({
                      ...reminderData,
                      second_reminder_in_days: isNaN(valueNumber) ? '' : valueNumber,
                    })
                  }
                  min={0}
                >
                  <NumberInputField placeholder="Enter days" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500">
                  Third Reminder (Days)
                </FormLabel>
                <NumberInput
                  value={reminderData.third_reminder_in_days || ''}
                  onChange={(valueString, valueNumber) =>
                    setReminderData({
                      ...reminderData,
                      third_reminder_in_days: isNaN(valueNumber) ? '' : valueNumber,
                    })
                  }
                  min={0}
                >
                  <NumberInputField placeholder="Enter days" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeReminder} isDisabled={isReminderLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleReminderSubmit}
              isLoading={isReminderLoading}
              loadingText="Saving..."
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Profile Modal */}
      <Modal isOpen={addProfileModal.isOpen} onClose={closeAddProfile} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Profile</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="500">
                  Select Category
                </FormLabel>
                <Select
                  placeholder={isProfileModalLoading ? 'Loading categories...' : 'Select category'}
                  value={profileCategory}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProfileCategory(value);
                    setProfileSlugId('');
                    fetchSlugsForCategory(value);
                  }}
                  isDisabled={isProfileModalLoading}
                >
                  {websiteCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="500">
                  Select Slug URL
                </FormLabel>
                <Select
                  placeholder={
                    !profileCategory
                      ? 'Select category first'
                      : isSlugLoading
                        ? 'Loading slugs...'
                        : 'Select slug URL'
                  }
                  value={profileSlugId}
                  onChange={(e) => setProfileSlugId(e.target.value)}
                  isDisabled={!profileCategory || isSlugLoading}
                >
                  {websiteSlugsByCategory.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.slug_link || item.full_slug || item.service_name}
                    </option>
                  ))}
                </Select>
                {profileCategory && !isSlugLoading && websiteSlugsByCategory.length === 0 && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    No slugs found for selected category.
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={closeAddProfile}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SendFreelancerEmailModal
        isOpen={emailModal.isOpen}
        onClose={() => {
          emailModal.onClose();
          setSelectedFreelancer(null);
        }}
        freelancer={selectedFreelancer}
      />
    </Box>
  );
}

// Modal component to send email to freelancer
const SendFreelancerEmailModal = ({ isOpen, onClose, freelancer }) => {
  const [emailForm, setEmailForm] = useState({
    subject: '',
    message: '',
  });
  const [isEmailSending, setIsEmailSending] = useState(false);

  // Reset form when opened or freelancer changes
  useEffect(() => {
    if (isOpen) {
      setEmailForm({
        subject: '',
        message: '',
      });
      setIsEmailSending(false);
    }
  }, [isOpen, freelancer?.id]);

  const handleSendEmail = async () => {
    if (!freelancer) return;

    const trimmedSubject = (emailForm.subject || '').trim();
    const trimmedMessage = (emailForm.message || '').trim();

    if (!trimmedSubject) {
      showError('Please enter email subject');
      return;
    }

    if (!trimmedMessage) {
      showError('Please enter email message');
      return;
    }

    setIsEmailSending(true);
    try {
      const payload = {
        subject: trimmedSubject,
        message: trimmedMessage,
      };

      const response = await postApi(
        `${apiEndPoints.SEND_EMAIL_TO_FREELANCER}/${freelancer.id}/send-email`,
        payload,
      );

      if (response?.data?.success) {
        showSuccess('Email sent successfully');
        onClose();
      } else {
        showError(response?.data?.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email to freelancer:', error);
      showError(error?.response?.data?.message || 'Failed to send email');
    } finally {
      setIsEmailSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Send Email to Freelancer</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="500">
                Subject
              </FormLabel>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm((prev) => ({
                  ...prev,
                  subject: e.target.value,
                }))}
                placeholder="Enter email subject"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="500">
                Message
              </FormLabel>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm((prev) => ({
                  ...prev,
                  message: e.target.value,
                }))}
                placeholder="Enter email message"
                rows={6}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isEmailSending}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSendEmail}
            isLoading={isEmailSending}
            loadingText="Sending..."
          >
            Send
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Freelancer Detail Content Component
export const FreelancerDetailContent = ({
  completeData,
  tabIndex,
  onTabChange,
  isEditMode = false,
  userId,
  detailVariant = 'default',
}) => {
  const dispatch = useDispatch();
  const textColor = useColorModeValue('rgb(32, 33, 36)', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const cardBg = useColorModeValue('white', 'navy.800');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const pageHeadlineBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  // State for editable fields
  const [formData, setFormData] = useState({
    primaryIntroduction: {},
    professionalExperience: {},
    projects: [],
    certifications: [],
    education: [],
  });
  
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Category and Subcategory states
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryIds, setSelectedSubCategoryIds] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [showTechnicalExpertise, setShowTechnicalExpertise] = useState(false);
  
  // Technologies states
  const [technologies, setTechnologies] = useState([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState([]);
  const [isLoadingTechnologies, setIsLoadingTechnologies] = useState(false);
  const [subCategorySearchTerm, setSubCategorySearchTerm] = useState('');
  const [technologySearchTerm, setTechnologySearchTerm] = useState('');
  
  // Languages states
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const languageOptions = [
    { value: 'English', label: 'English' },
    { value: 'Hindi', label: 'Hindi' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'French', label: 'French' },
    { value: 'German', label: 'German' },
    { value: 'Chinese', label: 'Chinese' },
    { value: 'Japanese', label: 'Japanese' },
    { value: 'Arabic', label: 'Arabic' },
    { value: 'Russian', label: 'Russian' },
    { value: 'Portuguese', label: 'Portuguese' }
  ];
  
  // Projects states
  const [projectTechnologies, setProjectTechnologies] = useState([]);
  const [projectIndustries, setProjectIndustries] = useState([]);
  const [projectCountryOptions, setProjectCountryOptions] = useState([]);
  const [hideApplicationTypeForProjects, setHideApplicationTypeForProjects] = useState(false);
  
  // Education states
  const [graduationList, setGraduationList] = useState([]);
  const [postGraduationList, setPostGraduationList] = useState([]);
  const [educationValidated, setEducationValidated] = useState(false);
  
  // Year generation functions
  const generateYearOptions = (startYear = 1960, endYear = new Date().getFullYear()) => {
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };
  
  const getTillYearOptions = (fromYear) => {
    if (!fromYear) return generateYearOptions();
    const currentYear = new Date().getFullYear();
    return generateYearOptions(parseInt(fromYear), currentYear);
  };
  
  // Get Post Graduate From year options (should start after Graduation Till year)
  const getPostGraduationFromYearOptions = () => {
    // Find the latest graduation till year
    let latestGraduationYear = 0;
    graduationList.forEach(graduation => {
      if (graduation.year && graduation.year !== '0' && parseInt(graduation.year) > latestGraduationYear) {
        latestGraduationYear = parseInt(graduation.year);
      }
    });
    
    const currentYear = new Date().getFullYear();
    const startYear = latestGraduationYear > 0 ? latestGraduationYear + 1 : 1960;
    return generateYearOptions(startYear, currentYear);
  };
  
  // Degree options
  const graduationDegreeOptions = [
    { value: '1', label: 'Any Engineering' },
    { value: '3', label: 'Any Management' },
    { value: '4', label: 'Any Computers(Degree/Diploma)' },
    { value: '6', label: 'Any Diploma Holders' },
    { value: '8', label: 'Bachelor of Arts' },
    { value: '10', label: 'Bachelor of Business Administration' },
    { value: '11', label: 'Bachelor of Commerce' },
    { value: '14', label: 'Bachelor of Science' },
    { value: '16', label: 'B.Tech/B.E.' },
    { value: '17', label: 'Bachelor of Computer Applications' },
    { value: '22', label: 'Fashion/Designing' },
    { value: '23', label: 'Journalism/Mass Communication' },
  ];
  
  const postGraduationDegreeOptions = [
    { value: '32', label: 'Any Engineering' },
    { value: '34', label: 'Any Management' },
    { value: '40', label: 'Journalism/Mass Comunication' },
    { value: '42', label: 'Master of Arts' },
    { value: '44', label: 'Master of Commerce' },
    { value: '47', label: 'MS/M.Sc' },
    { value: '48', label: 'Master of Technology' },
    { value: '50', label: 'MBA/PGDM' },
    { value: '52', label: 'Master of Computer Applications (M.C.A.)' },
    { value: '56', label: 'PG Diploma' },
    { value: '57', label: 'PR/Advertising' },
  ];
  
  const educationTypeOptions = [
    { value: '2', label: 'Full Time' },
    { value: '3', label: 'Part Time' },
    { value: '4', label: 'Correspondence' },
  ];
  
  // Validation functions
  const isGraduationValid = (graduation) => {
    return (
      graduation.degree &&
      graduation.university_name &&
      graduation.month &&
      graduation.year &&
      graduation.education_type
    );
  };
  
  const isPostGraduationValid = (postGraduation) => {
    return (
      postGraduation.degree &&
      postGraduation.university_name &&
      postGraduation.month &&
      postGraduation.year &&
      postGraduation.education_type
    );
  };
  
  const hasAnyPostGraduationData = () => {
    return postGraduationList.some(postGrad =>
      (postGrad.degree && postGrad.degree !== '') ||
      (postGrad.university_name && postGrad.university_name.trim() !== '') ||
      (postGrad.month && postGrad.month !== '') ||
      (postGrad.year && postGrad.year !== '') ||
      (postGrad.education_type && postGrad.education_type !== '')
    );
  };
  
  // Education handlers
  const handleAddGraduation = () => {
    setGraduationList([
      ...graduationList,
      {
        id: null,
        delete_id: '0',
        education_type: '',
        graduation_type: '3',
        university_name: '',
        month: '',
        year: '',
        degree: '',
      },
    ]);
  };
  
  const handleRemoveGraduation = (index, id) => {
    const list = [...graduationList];
    list.splice(index, 1);
    setGraduationList(list);
    
    // Update formData
    const allEducation = [...list, ...postGraduationList];
    setFormData(prev => ({
      ...prev,
      education: allEducation
    }));
  };
  
  const handleAddPostGraduation = () => {
    setPostGraduationList([
      ...postGraduationList,
      {
        id: null,
        delete_id: '0',
        education_type: '',
        graduation_type: '4',
        university_name: '',
        month: '',
        year: '',
        degree: '',
      },
    ]);
  };
  
  const handleRemovePostGraduation = (index, id) => {
    const list = [...postGraduationList];
    list.splice(index, 1);
    setPostGraduationList(list);
    
    // Update formData
    const allEducation = [...graduationList, ...list];
    setFormData(prev => ({
      ...prev,
      education: allEducation
    }));
  };
  
  const handleGraduationChange = (index, field, value) => {
    const list = [...graduationList];
    list[index][field] = value;
    
    // Reset year when month changes
    if (field === 'month') {
      list[index].year = '';
    }
    
    setGraduationList(list);
    
    // Update formData
    const allEducation = [...list, ...postGraduationList];
    setFormData(prev => ({
      ...prev,
      education: allEducation
    }));
  };
  
  const handlePostGraduationChange = (index, field, value) => {
    const list = [...postGraduationList];
    list[index][field] = value;
    
    // Reset year when month changes
    if (field === 'month') {
      list[index].year = '';
    }
    
    setPostGraduationList(list);
    
    // Update formData
    const allEducation = [...graduationList, ...list];
    setFormData(prev => ({
      ...prev,
      education: allEducation
    }));
  };
  
  // Project type options
  const projectTypeOptions = [
    { value: '1', label: 'Support' },
    { value: '2', label: 'New Development' },
    { value: '3', label: 'New Development Cum Support' },
  ];
  
  // Categories that should hide Application Type field
  const categoriesToHideApplicationType = [
    'UI & Design',
    'Marketing',
    'Content',
    'Infrastructure & Security',
    'Video & Animation'
  ];
  
  // Country, State, City dropdown states
  const countryOptions = useMemo(() => {
    return Country.getAllCountries().map(country => ({
      label: country.name,
      value: country.isoCode,
      phoneCode: country.phonecode
    }));
  }, []);
  
  const [countryValue, setCountryValue] = useState(null);
  const [stateValue, setStateValue] = useState(null);
  const [cityValue, setCityValue] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  
  // Initialize form data when completeData changes
  useEffect(() => {
    if (completeData && isEditMode) {
      setFormData({
        primaryIntroduction: completeData.primaryIntroduction || {},
        professionalExperience: completeData.professionalExperience || {},
        projects: completeData.projects?.data || [],
        certifications: completeData.certifications?.data || [],
        education: completeData.education?.data || [],
      });
      
      // Initialize country, state, city dropdowns
      const user = completeData.primaryIntroduction?.user;
      if (user) {
        let countryVal = null;
        let cityVal = null;
        let stateVal = null;
        
        // Handle country - can be ISO code string or object with id
        if (user.country) {
          if (typeof user.country === 'object' && user.country.id) {
            countryVal = user.country.id;
          } else if (typeof user.country === 'string') {
            // Check if it's already an ISO code or needs conversion
            countryVal = user.country;
          }
        }
        
        // Handle city
        if (user.city) {
          cityVal = user.city;
        }
        
        // Handle state - check both state and user_state fields
        if (user.user_state || user.state) {
          stateVal = user.user_state || user.state;
        }
        
        if (countryVal) {
          setCountryValue(countryVal);
          const countryStates = State.getStatesOfCountry(countryVal);
          const stateOptions = countryStates ? countryStates.map(s => ({ 
            label: s.name, 
            value: s.isoCode, 
            countryCode: s.countryCode 
          })) : [];
          setStates(stateOptions);
          
          if (stateVal) {
            setStateValue(stateVal);
            const selectedState = stateOptions.find(s => s.value === stateVal || s.label === stateVal);
            if (selectedState) {
              const stateCities = City.getCitiesOfState(countryVal, selectedState.value);
              const cityOptions = stateCities ? stateCities.map(city => ({ 
                label: city.name, 
                value: city.name, 
                stateCode: city.stateCode, 
                countryCode: city.countryCode 
              })) : [];
              setCities(cityOptions);
            }
          }
          
          if (cityVal) {
            setCityValue(cityVal);
          }
        } else {
          // Reset if no country
          setCountryValue(null);
          setStateValue(null);
          setCityValue(null);
          setStates([]);
          setCities([]);
        }
      }
    }
  }, [completeData, isEditMode]);
  
  // Country, State, City change handlers
  const handleCountryChange = (value) => {
    const selectedCountryData = countryOptions.find(country => country.value === value);
    setSelectedCountry(selectedCountryData);
    setCountryValue(value);
    setStateValue(null);
    setCityValue(null);
    setSelectedCity(null);

    // Load states for selected country
    const countryStates = State.getStatesOfCountry(value);
    const stateOptions = countryStates ? countryStates.map(st => ({
      label: st.name,
      value: st.isoCode,
      countryCode: st.countryCode
    })) : [];
    setStates(stateOptions);
    setCities([]);

    const displayData = formData.primaryIntroduction?.user || {};
    updateFormData('primaryIntroduction', 'user', {
      ...displayData,
      country: selectedCountryData?.value || '',
      state: '',
      city: ''
    });
  };

  const handleStateChange = (value) => {
    setStateValue(value);
    setCityValue(null);
    const selectedState = states.find(st => st.value === value);
    const stateCities = City.getCitiesOfState(countryValue, value);
    const cityOptions = stateCities ? stateCities.map(city => ({
      label: city.name,
      value: city.name,
      stateCode: city.stateCode,
      countryCode: city.countryCode
    })) : [];
    setCities(cityOptions);
    
    const displayData = formData.primaryIntroduction?.user || {};
    updateFormData('primaryIntroduction', 'user', {
      ...displayData,
      state: selectedState?.label || selectedState?.value || '',
      city: ''
    });
  };

  const handleCityChange = (value) => {
    const selectedCityData = cities.find(city => city.value === value);
    setSelectedCity(selectedCityData);
    setCityValue(value);
    
    const displayData = formData.primaryIntroduction?.user || {};
    updateFormData('primaryIntroduction', 'user', {
      ...displayData,
      city: selectedCityData?.label || ''
    });
  };
  
  // Fetch categories
  const fetchCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const response = await getApi(apiEndPoints.GET_CATEGORIES);
      
      if (Array.isArray(response?.data?.data)) {
        setCategories(response.data.data);
      } else if (Array.isArray(response?.data)) {
        setCategories(response.data);
      } else if (Array.isArray(response)) {
        setCategories(response);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };
  
  // Fetch subcategories
  const fetchSubCategories = async (categoryId) => {
    if (!categoryId) {
      setSubCategories([]);
      return;
    }
    try {
      const response = await getApi(`${apiEndPoints.GET_SUBCATEGORIES}/${categoryId}`);
      
      if (Array.isArray(response?.data?.data)) {
        setSubCategories(response.data.data);
      } else if (Array.isArray(response?.data)) {
        setSubCategories(response.data);
      } else if (Array.isArray(response)) {
        setSubCategories(response);
      } else {
        setSubCategories([]);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubCategories([]);
    }
  };
  
  // Handle category change
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryIds([]);
    
    const selectedCategory = categories.find(cat => cat.id.toString() === categoryId);
    const data = formData.professionalExperience?.data || {};
    
    // Check if the selected category should show Technical Expertise
    let shouldShowTechExpertise = false;
    if (selectedCategory) {
      const categoryName = selectedCategory.name.toLowerCase();
      shouldShowTechExpertise = 
        categoryName.includes('development') || 
        categoryName.includes('it') || 
        categoryName.includes('ai service') || 
        categoryName.includes('engineering');
    }
    
    setShowTechnicalExpertise(shouldShowTechExpertise);
    
    // Clear technical expertise if category doesn't require it
    if (!shouldShowTechExpertise) {
      setSelectedTechnologies([]);
      setTechnologySearchTerm('');
      updateFormData('professionalExperience', 'data', {
        ...data,
        project_category: categoryId,
        project_sub_category: '',
        technologty_pre: ''
      });
    } else {
      updateFormData('professionalExperience', 'data', {
        ...data,
        project_category: categoryId,
        project_sub_category: ''
      });
    }
    
    updateFormData('professionalExperience', 'category', selectedCategory || null);
    
    if (categoryId) {
      fetchSubCategories(categoryId);
    } else {
      setSubCategories([]);
    }
  };
  
  // Handle subcategory change (multi-select)
  const handleSubCategoryChange = (subCategoryId, isChecked) => {
    let updatedIds;
    if (isChecked) {
      updatedIds = [...selectedSubCategoryIds, subCategoryId];
    } else {
      updatedIds = selectedSubCategoryIds.filter(id => id !== subCategoryId);
    }
    setSelectedSubCategoryIds(updatedIds);
    
    const subCategoryString = updatedIds.join(',');
    const data = formData.professionalExperience?.data || {};
    updateFormData('professionalExperience', 'data', {
      ...data,
      project_sub_category: subCategoryString
    });
    
    // Update subCategory in formData
    const selectedSubCats = subCategories.filter(sub => updatedIds.includes(sub.id.toString()));
    updateFormData('professionalExperience', 'subCategory', selectedSubCats.length > 0 ? selectedSubCats[0] : null);
  };
  
  // Reset search terms when category changes
  useEffect(() => {
    if (selectedCategoryId) {
      setSubCategorySearchTerm('');
    }
  }, [selectedCategoryId]);
  
  // Fetch technologies
  const fetchTechnologies = async () => {
    try {
      setIsLoadingTechnologies(true);
      const response = await getApi(apiEndPoints.GET_TECHNOLOGIES);
      
      if (Array.isArray(response?.data?.data)) {
        setTechnologies(response.data.data);
      } else if (Array.isArray(response?.data)) {
        setTechnologies(response.data);
      } else if (Array.isArray(response)) {
        setTechnologies(response);
      } else {
        setTechnologies([]);
      }
    } catch (error) {
      console.error('Error fetching technologies:', error);
      setTechnologies([]);
    } finally {
      setIsLoadingTechnologies(false);
    }
  };
  
  // Handle technology change (multi-select)
  const handleTechnologyChange = (technologyName, isChecked) => {
    let updatedTechnologies;
    if (isChecked) {
      updatedTechnologies = [...selectedTechnologies, technologyName];
    } else {
      updatedTechnologies = selectedTechnologies.filter(tech => tech !== technologyName);
    }
    setSelectedTechnologies(updatedTechnologies);
    
    const technologyString = updatedTechnologies.join(',');
    const data = formData.professionalExperience?.data || {};
    updateFormData('professionalExperience', 'data', {
      ...data,
      technologty_pre: technologyString
    });
  };
  
  // Handle language change (multi-select)
  const handleLanguageChange = (languageValue, isChecked) => {
    let updatedLanguages;
    if (isChecked) {
      updatedLanguages = [...selectedLanguages, languageValue];
    } else {
      updatedLanguages = selectedLanguages.filter(lang => lang !== languageValue);
    }
    setSelectedLanguages(updatedLanguages);
    
    const languageString = updatedLanguages.join(',');
    const data = formData.professionalExperience?.data || {};
    updateFormData('professionalExperience', 'data', {
      ...data,
      languages: languageString
    });
  };
  
  // Fetch project technologies
  const fetchProjectTechnologies = async () => {
    try {
      const response = await getApi(apiEndPoints.GET_TECHNOLOGIES);
      
      if (Array.isArray(response?.data?.data)) {
        setProjectTechnologies(response.data.data.map(item => ({
          label: item.name,
          value: item.name,
        })));
      } else if (Array.isArray(response?.data)) {
        setProjectTechnologies(response.data.map(item => ({
          label: item.name,
          value: item.name,
        })));
      } else if (Array.isArray(response)) {
        setProjectTechnologies(response.map(item => ({
          label: item.name,
          value: item.name,
        })));
      } else {
        setProjectTechnologies([]);
      }
    } catch (error) {
      console.error('Error fetching project technologies:', error);
      setProjectTechnologies([]);
    }
  };
  
  // Fetch project industries
  const fetchProjectIndustries = async () => {
    try {
      const response = await getApi(apiEndPoints.GET_INDUSTRIES);
      
      if (Array.isArray(response?.data?.data)) {
        setProjectIndustries(response.data.data.map(item => ({
          label: item.industry,
          value: item.id.toString(),
        })));
      } else if (Array.isArray(response?.data)) {
        setProjectIndustries(response.data.map(item => ({
          label: item.industry,
          value: item.id.toString(),
        })));
      } else if (Array.isArray(response)) {
        setProjectIndustries(response.map(item => ({
          label: item.industry,
          value: item.id.toString(),
        })));
      } else {
        setProjectIndustries([]);
      }
    } catch (error) {
      console.error('Error fetching project industries:', error);
      setProjectIndustries([]);
    }
  };
  
  // Initialize project country options
  useEffect(() => {
    if (isEditMode) {
      const countries = Country.getAllCountries().map(country => ({
        value: country.name,
        label: country.name,
        isoCode: country.isoCode,
        phoneCode: country.phonecode
      }));
      setProjectCountryOptions(countries);
    }
  }, [isEditMode]);
  
  // Check category to determine if Application Type should be hidden
  useEffect(() => {
    if (completeData && isEditMode && categories.length > 0) {
      const profExp = completeData.professionalExperience;
      if (profExp?.data?.project_category) {
        const selectedCategory = categories.find(cat => 
          cat.id.toString() === profExp.data.project_category.toString()
        );
        if (selectedCategory) {
          const shouldHide = categoriesToHideApplicationType.includes(selectedCategory.name);
          setHideApplicationTypeForProjects(shouldHide);
        }
      }
    }
  }, [completeData, isEditMode, categories]);
  
  // Load categories, technologies, and project data on mount
  useEffect(() => {
    if (isEditMode) {
      fetchCategories();
      fetchTechnologies();
      fetchProjectTechnologies();
      fetchProjectIndustries();
    }
  }, [isEditMode]);
  
  // Initialize category, subcategory, and technologies when data loads
  useEffect(() => {
    if (completeData && isEditMode && categories.length > 0) {
      const profExp = completeData.professionalExperience;
      if (profExp?.data?.project_category) {
        const categoryId = profExp.data.project_category.toString();
        
        // Always set category when data loads (will auto-select in dropdown)
        setSelectedCategoryId(categoryId);
        
        // Check if the loaded category should show Technical Expertise
        const selectedCategory = categories.find(cat => cat.id.toString() === categoryId);
        if (selectedCategory) {
          const categoryName = selectedCategory.name.toLowerCase();
          const shouldShowTechExpertise = 
            categoryName.includes('development') || 
            categoryName.includes('it') || 
            categoryName.includes('ai service') || 
            categoryName.includes('engineering');
          setShowTechnicalExpertise(shouldShowTechExpertise);
        }
        
        // Fetch subcategories
        fetchSubCategories(categoryId);
      } else {
        // Reset if no category in data
        setSelectedCategoryId(null);
        setSelectedSubCategoryIds([]);
        setShowTechnicalExpertise(false);
      }
    }
  }, [completeData, isEditMode, categories]);
  
  // Initialize selected subcategories when subcategories are loaded
  useEffect(() => {
    if (completeData && isEditMode && subCategories.length > 0 && selectedCategoryId) {
      const profExp = completeData.professionalExperience;
      if (profExp?.data?.project_sub_category) {
        const subCategoryIds = profExp.data.project_sub_category.split(',').map(id => id.trim()).filter(id => id);
        // Validate that the IDs exist in the loaded subcategories
        const validIds = subCategoryIds.filter(id => 
          subCategories.some(sub => sub.id.toString() === id)
        );
        if (validIds.length > 0) {
          // Only update if different to avoid unnecessary re-renders
          const currentIds = selectedSubCategoryIds.sort().join(',');
          const newIds = validIds.sort().join(',');
          if (currentIds !== newIds) {
            setSelectedSubCategoryIds(validIds);
          }
        } else if (selectedSubCategoryIds.length > 0) {
          // Clear if no valid IDs found
          setSelectedSubCategoryIds([]);
        }
      } else if (selectedSubCategoryIds.length > 0) {
        // Clear if no subcategories in data
        setSelectedSubCategoryIds([]);
      }
    }
  }, [completeData, isEditMode, subCategories, selectedCategoryId]);
  
  // Initialize selected technologies when technologies are loaded
  useEffect(() => {
    if (completeData && isEditMode && technologies.length > 0) {
      const profExp = completeData.professionalExperience;
      if (profExp?.data?.technologty_pre) {
        const techArray = profExp.data.technologty_pre.split(',').map(tech => tech.trim()).filter(tech => tech);
        // Validate that the technologies exist in the loaded technologies
        const validTechs = techArray.filter(tech => 
          technologies.some(t => t.name === tech)
        );
        if (validTechs.length > 0) {
          // Only update if different to avoid unnecessary re-renders
          const currentTechs = selectedTechnologies.sort().join(',');
          const newTechs = validTechs.sort().join(',');
          if (currentTechs !== newTechs) {
            setSelectedTechnologies(validTechs);
          }
        } else if (selectedTechnologies.length > 0) {
          // Clear if no valid technologies found
          setSelectedTechnologies([]);
        }
      } else if (selectedTechnologies.length > 0) {
        // Clear if no technologies in data
        setSelectedTechnologies([]);
      }
    }
  }, [completeData, isEditMode, technologies]);
  
  // Initialize selected languages when data loads
  useEffect(() => {
    if (completeData && isEditMode) {
      const profExp = completeData.professionalExperience;
      if (profExp?.data?.languages) {
        const langArray = profExp.data.languages.split(',').map(lang => lang.trim()).filter(lang => lang);
        // Only update if different to avoid unnecessary re-renders
        const currentLangs = selectedLanguages.sort().join(',');
        const newLangs = langArray.sort().join(',');
        if (currentLangs !== newLangs) {
          setSelectedLanguages(langArray);
        }
      } else if (selectedLanguages.length > 0) {
        // Clear if no languages in data
        setSelectedLanguages([]);
      }
    }
  }, [completeData, isEditMode]);
  
  // Initialize education data when data loads
  useEffect(() => {
    if (completeData && isEditMode) {
      const eduData = completeData.education;
      if (eduData) {
        // Separate graduation and post-graduation
        const grad = (eduData.graduation || []).map(item => ({
          id: item.id || null,
          delete_id: item.id ? '1' : '0',
          education_type: item.education_type || '',
          graduation_type: item.graduation_type || '3',
          university_name: item.university_name || '',
          month: item.month || '',
          year: item.year || '',
          degree: item.degree || '',
        }));
        
        const postGrad = (eduData.postGraduation || []).map(item => ({
          id: item.id || null,
          delete_id: item.id ? '1' : '0',
          education_type: item.education_type || '',
          graduation_type: item.graduation_type || '4',
          university_name: item.university_name || '',
          month: item.month || '',
          year: item.year || '',
          degree: item.degree || '',
        }));
        
        setGraduationList(grad.length > 0 ? grad : [{
          id: null,
          delete_id: '0',
          education_type: '',
          graduation_type: '3',
          university_name: '',
          month: '',
          year: '',
          degree: '',
        }]);
        
        setPostGraduationList(postGrad.length > 0 ? postGrad : [{
          id: null,
          delete_id: '0',
          education_type: '',
          graduation_type: '4',
          university_name: '',
          month: '',
          year: '',
          degree: '',
        }]);
        
        // Update formData
        setFormData(prev => ({
          ...prev,
          education: [...grad, ...postGrad]
        }));
      } else {
        // Reset if no education data
        setGraduationList([{
          id: null,
          delete_id: '0',
          education_type: '',
          graduation_type: '3',
          university_name: '',
          month: '',
          year: '',
          degree: '',
        }]);
        setPostGraduationList([{
          id: null,
          delete_id: '0',
          education_type: '',
          graduation_type: '4',
          university_name: '',
          month: '',
          year: '',
          degree: '',
        }]);
      }
    } else if (!isEditMode) {
      // Reset when not in edit mode
      setGraduationList([]);
      setPostGraduationList([]);
      setEducationValidated(false);
    }
  }, [completeData, isEditMode]);
  
  const handleUpdate = async (tabName) => {
    if (!userId) {
      showError('No freelancer selected');
      return;
    }

    setIsUpdating(true);
    try {
      if (tabName === 'Primary Introduction') {
        const primaryIntro = formData.primaryIntroduction;
        const userData = primaryIntro?.user || {};
        
        // Prepare the payload with only the fields that can be updated
        const payload = {
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          mobile: userData.mobile,
          gender: userData.gender,
          dob: userData.dob,
          doi: userData.doi,
          company_name: userData.company_name,
          gst_number: userData.gst_number,
          country: countryValue,
          state: stateValue,
          city: cityValue,
          designation: userData.designation,
        };

        const response = await putApi(
          `${apiEndPoints.UPDATE_FREELANCER_PRIMARY_INTRODUCTION}/${userId}/primary-introduction`,
          payload
        );

        if (response?.data?.success) {
          showSuccess('Primary Introduction updated successfully');
          // Refresh the complete data
          dispatch(freelancerCompleteData({ userId }));
        } else {
          showError(response?.data?.message || 'Failed to update Primary Introduction');
        }
      } else if (tabName === 'Professional Experience') {
        const profExp = formData.professionalExperience;
        const profExpData = profExp?.data || {};
        
        // Prepare the payload with all professional experience fields
        const payload = {
          profile_headline: profExpData.profile_headline,
          project_category: selectedCategoryId || profExpData.project_category,
          project_sub_category: selectedSubCategoryIds.join(','), // Comma-separated string
          technologty_pre: selectedTechnologies.join(','), // Comma-separated string
          model_engagement: profExpData.model_engagement,
          rateperhour: profExpData.rateperhour,
          rateperhour_2: profExpData.rateperhour_2,
          currency: profExpData.currency,
          languages: profExpData.languages,
          support_project: profExpData.support_project,
          development_project: profExpData.development_project,
          upwork_platform: profExpData.upwork_platform || false,
          upwork_platform_profile_link: profExpData.upwork_platform_profile_link || '',
          fiver_platform: profExpData.fiver_platform || false,
          fiver_platform_profile_link: profExpData.fiver_platform_profile_link || '',
          freelancer_platform: profExpData.freelancer_platform || false,
          freelancer_platform_profile_link: profExpData.freelancer_platform_profile_link || '',
          truelancer_platform: profExpData.truelancer_platform || false,
          truelancer_platform_profile_link: profExpData.truelancer_platform_profile_link || '',
          pph_platform: profExpData.pph_platform || false,
          pph_platform_profile_link: profExpData.pph_platform_profile_link || '',
          other_platform: profExpData.other_platform || '',
          other_platform_profile_link: profExpData.other_platform_profile_link || '',
        };

        const profExpResponse = await putApi(
          `${apiEndPoints.UPDATE_FREELANCER_PROFESSIONAL_EXPERIENCE}/${userId}/professional-experience`,
          payload
        );

        if (profExpResponse?.data?.success) {
          showSuccess('Professional Experience updated successfully');
          // Refresh the complete data
          dispatch(freelancerCompleteData({ userId }));
        } else {
          showError(profExpResponse?.data?.message || 'Failed to update Professional Experience');
        }
      } else if (tabName === 'Projects') {
        const projectsData = formData.projects || [];
        
        // Validate minimum 3 projects requirement
        if (projectsData.length < 3) {
          showError('At least 3 projects are mandatory. Please provide at least 3 projects.');
          setIsUpdating(false);
          return;
        }
        
        // Prepare the payload with inputList array
        const payload = {
          inputList: projectsData.map(project => ({
            id: project.id || null,
            delete_id: project.delete_id || '',
            project_name: project.project_name || '',
            project_type: project.project_type || '',
            is_mobile_platform: project.is_mobile_platform || false,
            is_web_platform: project.is_web_platform || false,
            is_desktop_platform: project.is_desktop_platform || false,
            is_embedding_platform: project.is_embedding_platform || false,
            duration: project.duration || '',
            technologty_pre: project.technologty_pre || '',
            industry: project.industry || '',
            project_details: project.project_details || '',
            project_link: project.project_link || '',
            upload_file: project.upload_file || '',
            project_location: project.project_location || '',
            user_last_path: window.location.pathname
          }))
        };

        const projectsResponse = await putApi(
          `${apiEndPoints.UPDATE_FREELANCER_PROJECTS}/${userId}/projects`,
          payload
        );

        if (projectsResponse?.data?.success) {
          showSuccess('Projects updated successfully');
          // Refresh the complete data
          dispatch(freelancerCompleteData({ userId }));
        } else {
          showError(projectsResponse?.data?.message || 'Failed to update Projects');
        }
      } else if (tabName === 'Education') {
        // Combine graduation and post-graduation lists
        const allEducation = [...graduationList, ...postGraduationList];
        
        // Prepare the payload - send array directly (matching user-side API format)
        const payload = allEducation.map(edu => ({
          id: edu.id || null,
          delete_id: edu.delete_id || '0',
          education_type: edu.education_type || '',
          graduation_type: edu.graduation_type || '',
          university_name: edu.university_name || '',
          month: edu.month || '',
          year: edu.year || '',
          degree: edu.degree || '',
          user_last_path: window.location.pathname
        }));

        const educationResponse = await putApi(
          `${apiEndPoints.UPDATE_FREELANCER_EDUCATION}/${userId}/education`,
          payload
        );

        if (educationResponse?.data?.success) {
          showSuccess('Education updated successfully');
          // Refresh the complete data
          dispatch(freelancerCompleteData({ userId }));
        } else {
          showError(educationResponse?.data?.message || 'Failed to update Education');
        }
      } else {
        // TODO: Implement other tab updates
        showSuccess(`${tabName} updated successfully`);
      }
    } catch (error) {
      console.error(`Error updating ${tabName}:`, error);
      showError(error?.response?.data?.message || `Failed to update ${tabName}`);
    } finally {
      setIsUpdating(false);
    }
  };
  
  const updateFormData = (section, field, value) => {
    setFormData(prev => {
      if (field === 'user') {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            user: value,
          },
        };
      } else if (field === 'data') {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            data: value,
          },
        };
      }
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };
    });
  };
  
  const updateNestedFormData = (section, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  if (!completeData) {
    return (
      <Flex py={10} align="center" justify="center">
        <Text color="gray.500">No data available</Text>
      </Flex>
    );
  }

  const { primaryIntroduction, professionalExperience, projects, certifications, education, summary } = completeData;

  const formatCurrency = (amount, currency) => {
    if (!amount) return '--';
    return `${currency || '$'} ${amount}`;
  };

  const getStatusBadgeScheme = (status) => {
    const s = (status || '').toString().toLowerCase();
    if (s.includes('approved')) return 'green';
    if (s.includes('pending') || s.includes('incomplete')) return 'yellow';
    if (s.includes('reject')) return 'red';
    if (s.includes('suspend')) return 'orange';
    if (s.includes('review')) return 'blue';
    return 'purple';
  };

  const PageFieldRow = ({ label, value }) => {
    if (value === null || value === undefined || value === '' || value === '--') return null;
    return (
      <Box textAlign="left">
        <Text className="freelancer-detail-field-label" mb={1}>
          {label}
        </Text>
        <Text className="freelancer-detail-field-value" color={textColor} lineHeight="snug">
          {value}
        </Text>
      </Box>
    );
  };

  /** Technologies & Platforms chips — border/radius/padding live in freelancerDetailPage.css */
  const DetailOutlineChip = ({ children }) => (
    <Box
      as="span"
      className="freelancer-detail-outline-chip freelancer-detail-field-value"
    >
      {children}
    </Box>
  );

  const renderInfoItem = (icon, label, value, fullWidth = false) => {
    if (!value && value !== 0 && value !== false) return null;

    if (detailVariant === 'page') {
      return (
        <GridItem colSpan={{ base: 12, sm: fullWidth ? 12 : 6, lg: fullWidth ? 12 : 4 }}>
          <PageFieldRow label={label} value={value} />
        </GridItem>
      );
    }

    return (
      <GridItem colSpan={{ base: 12, md: fullWidth ? 12 : 6, lg: fullWidth ? 12 : 4 }}>
        <VStack align="start" spacing={1} mb={4}>
          <HStack spacing={2}>
            {icon}
            <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
              {label}
            </Text>
          </HStack>
          <Text fontSize="sm" fontWeight="500" color={textColor}>
            {value}
          </Text>
        </VStack>
      </GridItem>
    );
  };

  const renderEditableField = (icon, label, value, onChange, fullWidth = false, isTextarea = false) => {
    return (
      <GridItem colSpan={{ base: 12, md: fullWidth ? 12 : 6, lg: fullWidth ? 12 : 4 }}>
        <FormControl mb={4}>
          <FormLabel fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
            <HStack spacing={2}>
              {icon}
              <Text>{label}</Text>
            </HStack>
          </FormLabel>
          {isTextarea ? (
            <Textarea
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              fontSize="sm"
              borderColor={borderColor}
              _hover={{ borderColor: 'brand.500' }}
              _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px brand.500' }}
            />
          ) : (
            <Input
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              fontSize="sm"
              borderColor={borderColor}
              _hover={{ borderColor: 'brand.500' }}
              _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px brand.500' }}
            />
          )}
        </FormControl>
      </GridItem>
    );
  };

  const renderPrimaryIntroduction = () => {
    const data = isEditMode ? formData.primaryIntroduction : primaryIntroduction;
    if (!data) {
      return <Text color="gray.500">No primary introduction data available.</Text>;
    }
    
    const { user, designation } = data;
    const userData = user || {};
    const displayData = isEditMode ? formData.primaryIntroduction?.user || userData : userData;

    return (
      <VStack align="stretch" spacing={6}>
        {isEditMode ? (
          <div className="fdp-edit-card">
            <div className="fdp-edit-card-header">
              <div className="fdp-edit-card-header-icon"><MdPerson /></div>
              <span className="fdp-edit-card-title">Personal Information</span>
            </div>
            <div className="fdp-edit-card-body">
              {/* Avatar + Name row */}
              <div className="fdp-edit-profile-header">
                <div className="fdp-edit-avatar-col">
                  <Avatar
                    size="xl"
                    src={displayData.profile_image}
                    name={formatFreelancerDisplayName(displayData.first_name, displayData.last_name)}
                    // bg="brand.500"
                  />
                </div>
                <div className="fdp-edit-name-row">
                  <div className="fdp-form-field">
                    <label className="fdp-form-label">First Name</label>
                    <input
                      className="fdp-input"
                      value={displayData.first_name || ''}
                      onChange={(e) => updateFormData('primaryIntroduction', 'user', { ...displayData, first_name: e.target.value })}
                    />
                  </div>
                  <div className="fdp-form-field">
                    <label className="fdp-form-label">Last Name</label>
                    <input
                      className="fdp-input"
                      value={displayData.last_name || ''}
                      onChange={(e) => updateFormData('primaryIntroduction', 'user', { ...displayData, last_name: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Contact fields */}
              <div className="fdp-form-grid" style={{ marginBottom: 16 }}>
                <div className="fdp-form-field">
                  <label className="fdp-form-label"><MdEmail size={12} /> Email</label>
                  <input className="fdp-input" value={displayData.email || ''} onChange={(e) => updateFormData('primaryIntroduction', 'user', { ...displayData, email: e.target.value })} />
                </div>
                <div className="fdp-form-field">
                  <label className="fdp-form-label"><MdPhone size={12} /> Mobile</label>
                  <input className="fdp-input" value={displayData.mobile || ''} onChange={(e) => updateFormData('primaryIntroduction', 'user', { ...displayData, mobile: e.target.value })} />
                </div>
                <div className="fdp-form-field">
                  <label className="fdp-form-label"><MdPerson size={12} /> Gender</label>
                  <input className="fdp-input" value={displayData.gender || ''} onChange={(e) => updateFormData('primaryIntroduction', 'user', { ...displayData, gender: e.target.value })} />
                </div>
                <div className="fdp-form-field">
                  <label className="fdp-form-label"><MdBusiness size={12} /> Company</label>
                  <input className="fdp-input" value={displayData.company_name || ''} onChange={(e) => updateFormData('primaryIntroduction', 'user', { ...displayData, company_name: e.target.value })} />
                </div>
              </div>

              {/* Location row */}
              <div className="fdp-form-grid">
                <div className="fdp-form-field">
                  <label className="fdp-form-label"><MdLocationOn size={12} /> Country</label>
                  <FdpSelect
                    value={countryValue || ''}
                    onChange={handleCountryChange}
                    options={countryOptions}
                    placeholder="Select a country"
                  />
                </div>
                <div className="fdp-form-field">
                  <label className="fdp-form-label"><MdLocationOn size={12} /> State</label>
                  <FdpSelect
                    value={stateValue || ''}
                    onChange={handleStateChange}
                    options={states}
                    placeholder={states.length > 0 ? 'Select a state' : 'No states found'}
                    disabled={!countryValue || states.length === 0}
                  />
                </div>
                <div className="fdp-form-field">
                  <label className="fdp-form-label"><MdLocationOn size={12} /> City</label>
                  <FdpSelect
                    value={cityValue || ''}
                    onChange={handleCityChange}
                    options={cities}
                    placeholder={cities.length > 0 ? 'Select a city' : 'No cities found'}
                    disabled={!stateValue || cities.length === 0}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : detailVariant === 'page' ? (
          <div className="fdp-section-card">
            <div className="fdp-section-card-accent" />
            <div className="fdp-section-card-body">
              {/* Profile header */}
              <div className="fdp-profile-header">
                <div className="fdp-avatar-wrap">
                  <Avatar
                    size="xl"
                    src={displayData.profile_image}
                    name={formatFreelancerDisplayName(displayData.first_name, displayData.last_name)}
                  />
                </div>
                <div className="fdp-profile-meta">
                  <div className="fdp-profile-name">
                    {formatFreelancerDisplayName(displayData.first_name, displayData.last_name) || '—'}
                  </div>
                  <Badge
                    colorScheme={getStatusBadgeScheme(displayData.status)}
                    variant="solid"
                    fontSize="xs"
                    px={3}
                    py={1}
                    borderRadius="full"
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {displayData.status || 'Unknown'}
                  </Badge>
                </div>
              </div>

              <hr className="fdp-divider" />

              <div className="fdp-section-heading">Contact &amp; Profile</div>
              <div className="fdp-info-grid">
                {displayData.email && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Email</span>
                    <span className="freelancer-detail-field-value">{displayData.email}</span>
                  </div>
                )}
                {displayData.mobile && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Mobile</span>
                    <span className="freelancer-detail-field-value">{displayData.mobile}</span>
                  </div>
                )}
                {displayData.gender && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Gender</span>
                    <span className="freelancer-detail-field-value">{displayData.gender}</span>
                  </div>
                )}
                {designation?.name && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Legal Entity Type</span>
                    <span className="freelancer-detail-field-value">{designation.name}</span>
                  </div>
                )}
                {displayData.company_name && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Company</span>
                    <span className="freelancer-detail-field-value">{displayData.company_name}</span>
                  </div>
                )}
                {[displayData.city, displayData.state, displayData.country].filter(Boolean).length > 0 && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Location</span>
                    <span className="freelancer-detail-field-value">
                      {[displayData.city, displayData.state, displayData.country].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {displayData.address && (
                  <div className="fdp-info-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="freelancer-detail-field-label">Address</span>
                    <span className="freelancer-detail-field-value">{displayData.address}</span>
                  </div>
                )}
                {displayData.aboutme && (
                  <div className="fdp-info-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="freelancer-detail-field-label">About</span>
                    <span className="freelancer-detail-field-value">{displayData.aboutme}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Card bg={cardBg} p={6}>
            <VStack align="stretch" spacing={4}>
              <HStack spacing={4} align="start">
                <Avatar
                  size="xl"
                  src={displayData.profile_image}
                  name={formatFreelancerDisplayName(displayData.first_name, displayData.last_name)}
                  bg="brand.500"
                />
                <VStack align="start" spacing={2} flex={1}>
                  <Text fontSize="xl" fontWeight="bold" color={textColor}>
                    {formatFreelancerDisplayName(displayData.first_name, displayData.last_name) || '—'}
                  </Text>
                  <Badge
                    colorScheme={getStatusBadgeScheme(displayData.status)}
                    variant="solid"
                    fontSize="xs"
                  >
                    {displayData.status || 'Unknown'}
                  </Badge>
                </VStack>
              </HStack>

              <Divider />

              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                {renderInfoItem(<MdEmail />, 'Email', displayData.email)}
                {renderInfoItem(<MdPhone />, 'Mobile', displayData.mobile)}
                {renderInfoItem(<MdPerson />, 'Gender', displayData.gender)}
                {renderInfoItem(<MdBusiness />, 'Legal Entity Type', designation?.name)}
                {renderInfoItem(<MdBusiness />, 'Company', displayData.company_name)}
                {renderInfoItem(<MdLocationOn />, 'Location',
                  [displayData.city, displayData.state, displayData.country].filter(Boolean).join(', '),
                )}
                {displayData.address && renderInfoItem(<MdLocationOn />, 'Address', displayData.address, true)}
                {displayData.aboutme && renderInfoItem(<MdPerson />, 'About', displayData.aboutme, true)}
              </Grid>
            </VStack>
          </Card>
        )}
        {isEditMode && (
          <div className="fdp-edit-action-bar">
            <button
              className="fdp-save-btn"
              onClick={() => handleUpdate('Primary Introduction')}
              disabled={isUpdating}
            >
              {isUpdating ? 'Saving…' : 'Save Personal Information'}
            </button>
          </div>
        )}
      </VStack>
    );
  };

  const renderProfessionalExperience = () => {
    const data = isEditMode ? formData.professionalExperience?.data || professionalExperience?.data : professionalExperience?.data;
    const category = isEditMode ? formData.professionalExperience?.category || professionalExperience?.category : professionalExperience?.category;
    const subCategory = isEditMode ? formData.professionalExperience?.subCategory || professionalExperience?.subCategory : professionalExperience?.subCategory;
    
    if (!data) {
      return <Text color="gray.500">No professional experience data available.</Text>;
    }

    // Prepare sub category display string (supports multiple subcategories)
    const subCategoryDisplay = Array.isArray(subCategory)
      ? subCategory.map((sub) => sub?.name).filter(Boolean).join(', ')
      : subCategory?.name;

    if (!isEditMode && detailVariant === 'page') {
      const rateStr = data.rateperhour_2
        ? formatCurrency(data.rateperhour_2, data.currency?.split('-')[1])
        : null;
      const platformRows = [
        { name: 'Upwork', active: data.upwork_platform, link: data.upwork_platform_profile_link },
        { name: 'Fiverr', active: data.fiver_platform, link: data.fiver_platform_profile_link },
        { name: 'Freelancer', active: data.freelancer_platform, link: data.freelancer_platform_profile_link },
        { name: 'PeoplePerHour', active: data.pph_platform, link: data.pph_platform_profile_link },
        { name: 'Truelancer', active: data.truelancer_platform, link: data.truelancer_platform_profile_link },
        { name: data.other_platform || 'Other', active: !!data.other_platform, link: data.other_platform_profile_link },
      ].filter((p) => p.active);

      return (
        <div>
          {/* Professional overview card */}
          <div className="fdp-section-card" style={{ marginBottom: 20 }}>
            <div className="fdp-section-card-body">
              <div className="fdp-section-heading">Professional Overview</div>
              {data.profile_headline && (
                <p className="freelancer-detail-field-value" style={{ marginBottom: 16 }}>
                  {data.profile_headline}
                </p>
              )}
              <div className="fdp-info-grid" style={{ marginBottom: 16 }}>
                {category?.name && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Category</span>
                    <span className="freelancer-detail-field-value">{category.name}</span>
                  </div>
                )}
                {subCategoryDisplay && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Sub Category</span>
                    <span className="freelancer-detail-field-value">{subCategoryDisplay}</span>
                  </div>
                )}
                {rateStr && rateStr !== '--' && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Rate per Hour</span>
                    <span className="freelancer-detail-field-value">{rateStr}</span>
                  </div>
                )}
                {data.languages && (
                  <div className="fdp-info-item">
                    <span className="freelancer-detail-field-label">Languages</span>
                    <span className="freelancer-detail-field-value">{data.languages}</span>
                  </div>
                )}
              </div>
              {data.technologty_pre && (
                <div>
                  <span className="freelancer-detail-field-label" style={{ display: 'block', marginBottom: 8 }}>Technologies</span>
                  <div className="fdp-chips-row">
                    {data.technologty_pre.split(',').map((tech, idx) => (
                      <DetailOutlineChip key={idx}>{tech.trim()}</DetailOutlineChip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Platform links card */}
          <div className="fdp-section-card">
            <div className="fdp-section-card-body">
              <div className="fdp-section-heading">Platform Links</div>
              {platformRows.length === 0 ? (
                <p className="freelancer-detail-field-value" style={{ color: '#a0aec0' }}>No platform links on file.</p>
              ) : (
                <div>
                  {platformRows.map((platform, idx) => (
                    <div key={`${platform.name}-${idx}`} className="fdp-platform-row">
                      <span className="fdp-platform-dot" />
                      <span className="fdp-platform-name">{platform.name}</span>
                      {platform.link ? (
                        <a
                          href={platform.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fdp-platform-link"
                        >
                          {platform.link}
                        </a>
                      ) : (
                        <span className="freelancer-detail-field-value">Active (no URL provided)</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="fdp-edit-wrap">
        <div className="fdp-edit-card">
          <div className="fdp-edit-card-header">
            <div className="fdp-edit-card-header-icon"><MdWork /></div>
            <span className="fdp-edit-card-title">Professional Overview</span>
          </div>
          <div className="fdp-edit-card-body">
            {isEditMode ? (
              <>
                <div className="fdp-form-grid-2" style={{ marginBottom: 16 }}>
                  <div className="fdp-form-field">
                    <label className="fdp-form-label"><MdBusiness size={12} /> Project Category</label>
                    <FdpSelect
                      value={selectedCategoryId || ''}
                      onChange={(val) => handleCategoryChange({ target: { value: val } })}
                      options={categories.map((cat) => ({ value: String(cat.id), label: cat.name }))}
                      placeholder="Select a category"
                      disabled={isLoadingCategories}
                    />
                  </div>
                  <div className="fdp-form-field">
                    <label className="fdp-form-label"><MdBusiness size={12} /> Project Sub Category</label>
                    <FdpMultiSelect
                      selectedValues={selectedSubCategoryIds}
                      onChange={(val, checked) => handleSubCategoryChange(val, checked)}
                      options={subCategories.map((s) => ({ value: String(s.id), label: s.name }))}
                      placeholder={selectedCategoryId ? 'Select subcategories…' : 'Select a category first'}
                      searchPlaceholder="Search subcategories…"
                      disabled={!selectedCategoryId || subCategories.length === 0}
                    />
                  </div>
                </div>
                <div className="fdp-form-grid-2" style={{ marginBottom: 16 }}>
                  <div className="fdp-form-field fdp-form-col-full">
                    <label className="fdp-form-label"><MdWork size={12} /> Profile Headline</label>
                    <input className="fdp-input" value={data.profile_headline || ''} onChange={(e) => updateFormData('professionalExperience', 'data', { ...data, profile_headline: e.target.value })} />
                  </div>
                  <div className="fdp-form-field">
                    <label className="fdp-form-label"><MdAttachMoney size={12} /> Rate per Hour</label>
                    <input className="fdp-input" value={data.rateperhour_2 || ''} onChange={(e) => updateFormData('professionalExperience', 'data', { ...data, rateperhour_2: e.target.value })} />
                  </div>
                </div>
                <div className="fdp-form-field" style={{ marginBottom: 16 }}>
                  <label className="fdp-form-label"><MdLanguage size={12} /> Languages</label>
                  <div className="fdp-checkbox-inline-row">
                    {languageOptions.map((lang) => (
                      <label key={lang.value} className="fdp-checkbox-item" style={{ padding: '2px 4px' }}>
                        <input type="checkbox" checked={selectedLanguages.includes(lang.value)} onChange={(e) => handleLanguageChange(lang.value, e.target.checked)} />
                        {lang.label}
                      </label>
                    ))}
                  </div>
                </div>
                {showTechnicalExpertise && (
                  <div className="fdp-form-field">
                    <label className="fdp-form-label"><MdBusiness size={12} /> Technical Expertise</label>
                    <FdpMultiSelect
                      selectedValues={selectedTechnologies}
                      onChange={(val, checked) => handleTechnologyChange(val, checked)}
                      options={technologies.map((t) => ({ value: t.name, label: t.name }))}
                      placeholder={isLoadingTechnologies ? 'Loading…' : 'Select technologies…'}
                      searchPlaceholder="Search technologies…"
                      disabled={isLoadingTechnologies}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                  {data.profile_headline && renderInfoItem(<MdWork />, 'Profile Headline', data.profile_headline, true)}
                  <GridItem colSpan={{ base: 12, md: 6, lg: 4 }}>
                    <VStack align="start" spacing={1} mb={4}>
                      <HStack spacing={2}><MdBusiness /><Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">Category</Text></HStack>
                      <Text fontSize="sm" fontWeight="500" color={textColor}>{category?.name || '--'}</Text>
                      {subCategoryDisplay && (<><Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" mt={4}>Sub Category</Text><Text fontSize="sm" fontWeight="500" color={textColor}>{subCategoryDisplay}</Text></>)}
                    </VStack>
                  </GridItem>
                  {renderInfoItem(<MdAttachMoney />, 'Rate per Hour', formatCurrency(data.rateperhour_2, data.currency?.split('-')[1]))}
                  {renderInfoItem(<MdLanguage />, 'Languages', data.languages)}
                </Grid>
                {data.technologty_pre && (
                  <Box mt={4}>
                    <HStack spacing={2} mb={2}><MdBusiness size={20} /><Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">Technologies</Text></HStack>
                    <HStack spacing={2} flexWrap="wrap">
                      {data.technologty_pre.split(',').map((tech, index) => (<Tag key={index} colorScheme="brand" size="sm" borderRadius="md">{tech.trim()}</Tag>))}
                    </HStack>
                  </Box>
                )}
              </>
            )}
          </div>
        </div>
        <div className="fdp-edit-card">
          <div className="fdp-edit-card-header">
            <div className="fdp-edit-card-header-icon"><MdPublic /></div>
            <span className="fdp-edit-card-title">Platform Links</span>
          </div>
          <div className="fdp-edit-card-body">
            {isEditMode ? (
              <div className="fdp-form-grid-2">
                {[
                  { label: 'Upwork', field: 'upwork_platform_profile_link' },
                  { label: 'Fiverr', field: 'fiver_platform_profile_link' },
                  { label: 'Freelancer', field: 'freelancer_platform_profile_link' },
                  { label: 'PeoplePerHour', field: 'pph_platform_profile_link' },
                  { label: 'Truelancer', field: 'truelancer_platform_profile_link' },
                  { label: 'Other Platform', field: 'other_platform_profile_link' },
                ].map(({ label, field }) => (
                  <div key={field} className="fdp-form-field">
                    <label className="fdp-form-label"><MdLink size={12} /> {label}</label>
                    <input className="fdp-input" value={data[field] || ''} onChange={(e) => updateFormData('professionalExperience', 'data', { ...data, [field]: e.target.value })} placeholder="https://..." />
                  </div>
                ))}
              </div>
            ) : (
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                {[
                  { name: 'Upwork', active: data.upwork_platform, link: data.upwork_platform_profile_link },
                  { name: 'Fiverr', active: data.fiver_platform, link: data.fiver_platform_profile_link },
                  { name: 'Freelancer', active: data.freelancer_platform, link: data.freelancer_platform_profile_link },
                  { name: 'PeoplePerHour', active: data.pph_platform, link: data.pph_platform_profile_link },
                  { name: 'Truelancer', active: data.truelancer_platform, link: data.truelancer_platform_profile_link },
                  { name: data.other_platform || 'Other', active: !!data.other_platform, link: data.other_platform_profile_link },
                ].filter((p) => p.active).map((platform, index) => (
                  <GridItem key={index}>
                    <VStack align="start" spacing={1}>
                      <HStack spacing={2}><MdLink size={16} /><Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">{platform.name}</Text></HStack>
                      {platform.link ? (<Link href={platform.link} target="_blank" rel="noopener noreferrer" color="brand.500" fontSize="sm" isExternal>{platform.link}</Link>) : (<Text fontSize="sm" color={textColor}>Active (No link provided)</Text>)}
                    </VStack>
                  </GridItem>
                ))}
              </Grid>
            )}
          </div>
        </div>
        {isEditMode && (
          <div className="fdp-edit-action-bar">
            <button className="fdp-save-btn" onClick={() => handleUpdate('Professional Experience')} disabled={isUpdating}>
              {isUpdating ? 'Saving…' : 'Save Professional Experience'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Handle project technology change (multi-select per project)
  const handleProjectTechnologyChange = (projectIndex, technologyName, isChecked) => {
    const projects = [...(formData.projects || [])];
    const project = projects[projectIndex] || {};
    const currentTechs = project.technologty_pre ? project.technologty_pre.split(',').map(t => t.trim()).filter(t => t) : [];
    
    let updatedTechs;
    if (isChecked) {
      updatedTechs = [...currentTechs, technologyName];
    } else {
      updatedTechs = currentTechs.filter(tech => tech !== technologyName);
    }
    
    projects[projectIndex] = {
      ...project,
      technologty_pre: updatedTechs.join(',')
    };
    
    setFormData(prev => ({
      ...prev,
      projects
    }));
  };

  const renderProjects = () => {
    const projectsData = isEditMode ? formData.projects : projects?.data;
    const count = isEditMode ? formData.projects?.length : projects?.count;
    
    if (!projectsData || projectsData.length === 0) {
      return <Text color="gray.500">No projects data available.</Text>;
    }

    const getProjectTypeLabel = (projectType) => {
      if (projectType === '1') return 'Support';
      if (projectType === '2') return 'New Development';
      if (projectType === '3') return 'New Development Cum Support';
      return '--';
    };

    if (!isEditMode && detailVariant === 'page') {
      return (
        <div>
          <div className="fdp-section-title-row">
            <span className="fdp-section-title">Projects</span>
            <span className="fdp-count-badge">{count || projectsData.length}</span>
          </div>
          <div>
            {projectsData.map((project, index) => {
              const platforms = [
                { name: 'Web', active: project.is_web_platform },
                { name: 'Mobile', active: project.is_mobile_platform },
                { name: 'Desktop', active: project.is_desktop_platform },
                { name: 'Embedding', active: project.is_embedding_platform },
              ].filter((platform) => platform.active);
              const hasPlatforms = platforms.length > 0;
              const projectTechs = project.technologty_pre
                ? project.technologty_pre.split(',').map((t) => t.trim()).filter(Boolean)
                : [];

              return (
                <div key={project.id || index} className="fdp-project-card" style={{ marginBottom: 16 }}>
                  <div className="fdp-project-card-header">
                    <span className="fdp-project-name">
                      {project.project_name || `Project ${index + 1}`}
                    </span>
                    {project.project_type && (
                      <span className="fdp-project-type-badge">
                        {getProjectTypeLabel(project.project_type)}
                      </span>
                    )}
                  </div>
                  <div className="fdp-project-card-body">
                    <div className="fdp-project-meta-grid">
                      {project.duration && (
                        <div className="fdp-info-item">
                          <span className="freelancer-detail-field-label">Duration</span>
                          <span className="freelancer-detail-field-value">{project.duration} months</span>
                        </div>
                      )}
                      {project.project_location && (
                        <div className="fdp-info-item">
                          <span className="freelancer-detail-field-label">Location</span>
                          <span className="freelancer-detail-field-value">{project.project_location}</span>
                        </div>
                      )}
                      {project.industry && (
                        <div className="fdp-info-item">
                          <span className="freelancer-detail-field-label">Industry</span>
                          <span className="freelancer-detail-field-value">{project.industry}</span>
                        </div>
                      )}
                    </div>
                    {projectTechs.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <span className="freelancer-detail-field-label" style={{ display: 'block', marginBottom: 6 }}>Technologies</span>
                        <div className="fdp-chips-row">
                          {projectTechs.map((tech, techIndex) => (
                            <DetailOutlineChip key={techIndex}>{tech}</DetailOutlineChip>
                          ))}
                        </div>
                      </div>
                    )}
                    {hasPlatforms && (
                      <div style={{ marginBottom: 12 }}>
                        <span className="freelancer-detail-field-label" style={{ display: 'block', marginBottom: 6 }}>Platforms</span>
                        <div className="fdp-chips-row">
                          {platforms.map((platform, platformIndex) => (
                            <DetailOutlineChip key={platformIndex}>{platform.name}</DetailOutlineChip>
                          ))}
                        </div>
                      </div>
                    )}
                    {project.project_details && (
                      <p className="fdp-project-description">{project.project_details}</p>
                    )}
                    {project.project_link && (
                      <a
                        href={project.project_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fdp-platform-link"
                        style={{ display: 'inline-block', marginTop: 8 }}
                      >
                        View project →
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="fdp-edit-wrap">
        <div className="fdp-edit-card">
          <div className="fdp-edit-card-header">
            <div className="fdp-edit-card-header-icon"><MdFolder /></div>
            <span className="fdp-edit-card-title">Projects</span>
            <span className="fdp-count-badge" style={{ marginLeft: 8 }}>{count || projectsData.length}</span>
          </div>
          <div className="fdp-edit-card-body">
            {projectsData.map((project, index) => {
              const platforms = [
                { name: 'Web', active: project.is_web_platform },
                { name: 'Mobile', active: project.is_mobile_platform },
                { name: 'Desktop', active: project.is_desktop_platform },
                { name: 'Embedding', active: project.is_embedding_platform }
              ].filter(platform => platform.active);
              
              const hasPlatforms = platforms.length > 0;
              const projectTechs = project.technologty_pre ? project.technologty_pre.split(',').map(t => t.trim()).filter(t => t) : [];

              return (
                <div key={project.id || index} className="fdp-project-edit-card">
                  <div className="fdp-project-edit-card-header">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span className="fdp-project-edit-card-num">{index + 1}</span>
                      <span className="fdp-project-edit-card-label">
                        {project.project_name || `Project ${index + 1}`}
                      </span>
                    </div>
                  </div>
                  <div className="fdp-project-edit-card-body">
                    {isEditMode ? (
                      <>
                        <div className="fdp-form-grid-2">
                          <div className="fdp-form-field">
                            <label className="fdp-form-label">Project Name <span className="fdp-form-label-required">*</span></label>
                            <input className="fdp-input" value={project.project_name || ''} maxLength={100} onChange={(e) => { if (e.target.value.length <= 100) updateNestedFormData('projects', index, 'project_name', e.target.value); }} />
                          </div>
                          <div className="fdp-form-field">
                            <label className="fdp-form-label">Project Type <span className="fdp-form-label-required">*</span></label>
                            <FdpSelect
                              value={project.project_type || ''}
                              onChange={(val) => updateNestedFormData('projects', index, 'project_type', val)}
                              options={projectTypeOptions}
                              placeholder="Select Project Type"
                            />
                          </div>
                        </div>

                        {!hideApplicationTypeForProjects && (
                          <div className="fdp-form-field" style={{ marginTop: 12 }}>
                            <label className="fdp-form-label">Application Type <span className="fdp-form-label-required">*</span></label>
                            <div className="fdp-checkbox-inline-row">
                              {[
                                { label: 'Mobile App', field: 'is_mobile_platform' },
                                { label: 'Website', field: 'is_web_platform' },
                                { label: 'Desktop App', field: 'is_desktop_platform' },
                                { label: 'Embedded', field: 'is_embedding_platform' },
                              ].map(({ label, field }) => (
                                <label key={field} className="fdp-checkbox-item" style={{ padding: '2px 4px' }}>
                                  <input type="checkbox" checked={project[field] || false} onChange={(e) => updateNestedFormData('projects', index, field, e.target.checked)} />
                                  {label}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="fdp-form-grid-2" style={{ marginTop: 12 }}>
                          <div className="fdp-form-field">
                            <label className="fdp-form-label">Duration (Months) <span className="fdp-form-label-required">*</span></label>
                            <input className="fdp-input" type="text" value={project.duration || ''} maxLength={2} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); if (v === '' || parseInt(v) <= 50) updateNestedFormData('projects', index, 'duration', v); }} />
                          </div>
                          {!hideApplicationTypeForProjects && (
                            <div className="fdp-form-field">
                              <label className="fdp-form-label">Technology <span className="fdp-form-label-required">*</span></label>
                              <FdpMultiSelect
                                selectedValues={projectTechs}
                                onChange={(val, checked) => handleProjectTechnologyChange(index, val, checked)}
                                options={projectTechnologies}
                                placeholder="Select technologies…"
                                searchPlaceholder="Search technologies…"
                              />
                            </div>
                          )}
                        </div>

                        <div className="fdp-form-grid-2" style={{ marginTop: 12 }}>
                          <div className="fdp-form-field">
                            <label className="fdp-form-label">Industry <span className="fdp-form-label-required">*</span></label>
                            <FdpSelect
                              value={project.industry ? project.industry.toString() : ''}
                              onChange={(val) => updateNestedFormData('projects', index, 'industry', val)}
                              options={projectIndustries}
                              placeholder="Select Industry"
                            />
                          </div>
                          <div className="fdp-form-field">
                            <label className="fdp-form-label">Project Location <span className="fdp-form-label-required">*</span></label>
                            <FdpSelect
                              value={project.project_location || ''}
                              onChange={(val) => updateNestedFormData('projects', index, 'project_location', val)}
                              options={projectCountryOptions}
                              placeholder="Select Location"
                            />
                          </div>
                        </div>

                        <div className="fdp-form-field" style={{ marginTop: 12 }}>
                          <label className="fdp-form-label">
                            Project Details <span className="fdp-form-label-required">*</span>
                            <span className="fdp-form-hint">(Min 100, Max 300 chars)</span>
                          </label>
                          <textarea className="fdp-textarea" value={project.project_details || ''} rows={4} maxLength={300} onChange={(e) => { if (e.target.value.length <= 300) updateNestedFormData('projects', index, 'project_details', e.target.value); }} />
                          <div className={`fdp-char-counter${project.project_details?.length > 300 ? ' over' : ''}`}>
                            {project.project_details?.length || 0}/300
                          </div>
                        </div>

                        <div className="fdp-form-field" style={{ marginTop: 12 }}>
                          <label className="fdp-form-label">Project URL</label>
                          <input className="fdp-input" value={project.project_link || ''} maxLength={50} onChange={(e) => { if (e.target.value.length <= 50) updateNestedFormData('projects', index, 'project_link', e.target.value); }} placeholder="https://..." />
                        </div>
                      </>
                    ) : (
                      <>
                        <Text fontSize="md" fontWeight="bold" color={textColor}>
                          {project.project_name || `Project ${index + 1}`}
                        </Text>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {renderInfoItem(<MdCalendarToday />, 'Duration', project.duration ? `${project.duration} months` : null)}
                          {renderInfoItem(<MdLocationOn />, 'Location', project.project_location)}
                          {renderInfoItem(<MdWork />, 'Type', getProjectTypeLabel(project.project_type))}
                        </Grid>
                        {project.technologty_pre && (
                          <Box>
                            <HStack spacing={2} mb={2}>
                              <MdBusiness size={16} />
                              <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
                                Technologies
                              </Text>
                            </HStack>
                            <HStack spacing={2} flexWrap="wrap">
                              {projectTechs.map((tech, techIndex) => (
                                <Tag key={techIndex} colorScheme="brand" size="sm" borderRadius="md">
                                  {tech}
                                </Tag>
                              ))}
                            </HStack>
                          </Box>
                        )}
                        {hasPlatforms && (
                          <Box>
                            <HStack spacing={2} mb={2}>
                              <MdPublic size={16} />
                              <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
                                Platforms
                              </Text>
                            </HStack>
                            <HStack spacing={2} flexWrap="wrap">
                              {platforms.map((platform, platformIndex) => (
                                <Tag key={platformIndex} variant="outlined" size="sm" borderRadius="md">
                                  {platform.name}
                                </Tag>
                              ))}
                            </HStack>
                          </Box>
                        )}
                        {project.project_details && (
                          <Box>
                            <HStack spacing={2} mb={2}>
                              <MdDescription size={16} />
                              <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
                                Description
                              </Text>
                            </HStack>
                            <Text fontSize="sm" color={textColor} lineHeight="1.6">
                              {project.project_details}
                            </Text>
                          </Box>
                        )}
                        {project.project_link && (
                          <Link 
                            href={project.project_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            color="brand.500"
                            fontSize="sm"
                            isExternal
                          >
                            <HStack spacing={1}>
                              <MdLink />
                              <Text>View Project</Text>
                            </HStack>
                          </Link>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {isEditMode && (
          <div className="fdp-edit-action-bar">
            <button className="fdp-save-btn" onClick={() => handleUpdate('Projects')} disabled={isUpdating}>
              {isUpdating ? 'Saving…' : 'Save Projects'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCertifications = () => {
    const certsData = isEditMode ? formData.certifications : certifications?.data;
    const count = isEditMode ? formData.certifications?.length : certifications?.count;
    
    if (!certsData || certsData.length === 0) {
      return <Text color="gray.500">No certifications data available.</Text>;
    }

    const isPageViewCert = !isEditMode && detailVariant === 'page';
    const certCellAlign = isPageViewCert ? 'left' : undefined;

    if (isPageViewCert) {
      return (
        <div>
          <div className="fdp-section-title-row">
            <span className="fdp-section-title">Certifications</span>
            <span className="fdp-count-badge">{count || certsData.length}</span>
          </div>
          <div className="fdp-cert-table-wrap">
            <table className="fdp-cert-table">
              <thead>
                <tr>
                  <th>Certificate Name</th>
                  <th>Institution</th>
                  <th>Certificate No.</th>
                  <th>From</th>
                  <th>Till</th>
                </tr>
              </thead>
              <tbody>
                {certsData.map((cert, index) => (
                  <tr key={cert.certificate_id || index}>
                    <td>{cert.name || '--'}</td>
                    <td>{cert.institutename || '--'}</td>
                    <td>{cert.certificate_no || '--'}</td>
                    <td>{cert.from_date || '--'}</td>
                    <td>{cert.till_date === '0' ? 'Present' : cert.till_date || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <VStack align="stretch" spacing={4} w="100%">
        <Card bg={cardBg} p={6} borderWidth="1px" borderColor={borderColor} borderRadius="lg" boxShadow="none">
          <HStack spacing={2} mb={4}>
            <MdCardMembership size={24} color="brand.500" />
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              Certifications ({count || certsData.length})
            </Text>
          </HStack>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor} color={textColor}>Certificate Name</Th>
                  <Th borderColor={borderColor} color={textColor}>Institution</Th>
                  <Th borderColor={borderColor} color={textColor}>Certificate No.</Th>
                  <Th borderColor={borderColor} color={textColor}>From</Th>
                  <Th borderColor={borderColor} color={textColor}>Till</Th>
                </Tr>
              </Thead>
              <Tbody>
                {certsData.map((cert, index) => (
                  <Tr key={cert.certificate_id || index} _hover={{ bg: hoverBg }}>
                    {isEditMode ? (
                      <>
                        <Td borderColor={borderColor}>
                          <Input value={cert.name || ''} onChange={(e) => updateNestedFormData('certifications', index, 'name', e.target.value)} fontSize="sm" borderColor={borderColor} size="sm" />
                        </Td>
                        <Td borderColor={borderColor}>
                          <Input value={cert.institutename || ''} onChange={(e) => updateNestedFormData('certifications', index, 'institutename', e.target.value)} fontSize="sm" borderColor={borderColor} size="sm" />
                        </Td>
                        <Td borderColor={borderColor}>
                          <Input value={cert.certificate_no || ''} onChange={(e) => updateNestedFormData('certifications', index, 'certificate_no', e.target.value)} fontSize="sm" borderColor={borderColor} size="sm" />
                        </Td>
                        <Td borderColor={borderColor}>
                          <Input value={cert.from_date || ''} onChange={(e) => updateNestedFormData('certifications', index, 'from_date', e.target.value)} fontSize="sm" borderColor={borderColor} size="sm" />
                        </Td>
                        <Td borderColor={borderColor}>
                          <Input value={cert.till_date === '0' ? '' : cert.till_date || ''} onChange={(e) => updateNestedFormData('certifications', index, 'till_date', e.target.value)} fontSize="sm" borderColor={borderColor} size="sm" placeholder="Present" />
                        </Td>
                      </>
                    ) : (
                      <>
                        <Td borderColor={borderColor} color={textColor}>{cert.name || '--'}</Td>
                        <Td borderColor={borderColor} color={textColor}>{cert.institutename || '--'}</Td>
                        <Td borderColor={borderColor} color={textColor}>{cert.certificate_no || '--'}</Td>
                        <Td borderColor={borderColor} color={textColor}>{cert.from_date || '--'}</Td>
                        <Td borderColor={borderColor} color={textColor}>{cert.till_date === '0' ? 'Present' : cert.till_date || '--'}</Td>
                      </>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
        {isEditMode && (
          <div className="fdp-edit-action-bar">
            <button className="fdp-save-btn" onClick={() => handleUpdate('Certifications')} disabled={isUpdating}>
              {isUpdating ? 'Saving…' : 'Save Certifications'}
            </button>
          </div>
        )}
      </VStack>
    );

  };

  const renderEducation = () => {
    const graduation = isEditMode ? graduationList : education?.graduation || [];
    const postGraduation = isEditMode ? postGraduationList : education?.postGraduation || [];
    
    if (!isEditMode && graduation.length === 0 && postGraduation.length === 0) {
      return <Text color="gray.500">No education data available.</Text>;
    }

    if (!isEditMode && detailVariant === 'page') {
      const formatEduDegree = (edu, isPost) => {
        const opts = isPost ? postGraduationDegreeOptions : graduationDegreeOptions;
        return opts.find((opt) => opt.value === edu.degree)?.label || edu.degree || null;
      };
      const formatEduType = (edu) =>
        educationTypeOptions.find((opt) => opt.value === edu.education_type)?.label || edu.education_type || null;
      const formatEduYear = (edu) =>
        edu.year === '0' ? 'Pursuing' : edu.month && edu.year ? `${edu.month} - ${edu.year}` : null;

      const renderEduGroup = (list, isPost, groupLabel) => {
        if (!list || list.length === 0) return null;
        return (
          <div style={{ marginBottom: 24 }}>
            <div className="fdp-section-title-row">
              <span className="fdp-section-title">{groupLabel}</span>
              <span className="fdp-count-badge">{list.length}</span>
            </div>
            {list.map((edu, index) => (
              <div key={edu.id || index} className="fdp-edu-card">
                <div className="fdp-edu-icon">
                  <MdSchool />
                </div>
                <div className="fdp-edu-content">
                  <div className="fdp-edu-degree">{formatEduDegree(edu, isPost) || '—'}</div>
                  {edu.university_name && (
                    <div className="fdp-edu-university">{edu.university_name}</div>
                  )}
                  <div className="fdp-edu-meta">
                    <Text className='year'>{formatEduYear(edu) && <span>{formatEduYear(edu)}</span>}</Text>
                    {formatEduType(edu) && (
                      <span className="fdp-edu-type-badge">{formatEduType(edu)}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      };

      return (
        <div>
          {renderEduGroup(graduation, false, 'Graduation')}
          {renderEduGroup(postGraduation, true, 'Post Graduation')}
        </div>
      );
    }

    const renderGraduationEntry = (element, i) => {
      return (
        <div key={i} className="fdp-project-edit-card" style={{ marginBottom: 12 }}>
          <div className="fdp-project-edit-card-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="fdp-project-edit-card-num">{i + 1}</span>
              <span className="fdp-project-edit-card-label">Graduation Entry</span>
            </div>
          </div>
          <div className="fdp-project-edit-card-body">
            <div className="fdp-form-grid-2" style={{ marginBottom: 14 }}>
              <div className="fdp-form-field">
                <label className="fdp-form-label">Bachelor's Degree <span className="fdp-form-label-required">*</span></label>
                <FdpSelect
                  value={element.degree || ''}
                  onChange={(val) => handleGraduationChange(i, 'degree', val)}
                  options={graduationDegreeOptions}
                  placeholder="Select Bachelor's degree"
                />
                {educationValidated && (!element.degree || element.degree === '') && (
                  <span style={{ fontSize: 11, color: '#e53e3e', marginTop: 3, display: 'block' }}>Please select a bachelor's degree.</span>
                )}
              </div>
              <div className="fdp-form-field">
                <label className="fdp-form-label">University Name <span className="fdp-form-label-required">*</span></label>
                <input className="fdp-input" type="text" value={element.university_name || ''} maxLength={50} onChange={(e) => { if (e.target.value.length <= 50) handleGraduationChange(i, 'university_name', e.target.value); }} />
                {educationValidated && (!element.university_name || element.university_name.trim() === '') && (
                  <span style={{ fontSize: 11, color: '#e53e3e', marginTop: 3, display: 'block' }}>Please provide a valid university name.</span>
                )}
              </div>
            </div>
            <div className="fdp-form-grid-2">
              <div className="fdp-form-field">
                <label className="fdp-form-label">Year of Graduation <span className="fdp-form-label-required">*</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <FdpSelect
                    value={element.month || ''}
                    onChange={(val) => handleGraduationChange(i, 'month', val)}
                    options={generateYearOptions().map((y) => ({ value: String(y), label: String(y) }))}
                    placeholder="From"
                  />
                  <FdpSelect
                    value={element.year || ''}
                    onChange={(val) => handleGraduationChange(i, 'year', val)}
                    options={[{ value: '0', label: 'Pursuing' }, ...(element.month ? getTillYearOptions(element.month).map((y) => ({ value: String(y), label: String(y) })) : [])]}
                    placeholder="Till"
                    disabled={!element.month}
                  />
                </div>
                {educationValidated && ((!element.month || element.month === '') || (!element.year || element.year === '')) && (
                  <span style={{ fontSize: 11, color: '#e53e3e', marginTop: 3, display: 'block' }}>Please select both from and till years.</span>
                )}
              </div>
              <div className="fdp-form-field">
                <label className="fdp-form-label">Education Type <span className="fdp-form-label-required">*</span></label>
                <FdpSelect
                  value={element.education_type || ''}
                  onChange={(val) => handleGraduationChange(i, 'education_type', val)}
                  options={educationTypeOptions}
                  placeholder="Select Education Type"
                />
                {educationValidated && (!element.education_type || element.education_type === '') && (
                  <span style={{ fontSize: 11, color: '#e53e3e', marginTop: 3, display: 'block' }}>Please select an education type.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

    const renderPostGraduationEntry = (element, i) => {
      return (
        <div key={i} className="fdp-project-edit-card" style={{ marginBottom: 12 }}>
          <div className="fdp-project-edit-card-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="fdp-project-edit-card-num">{i + 1}</span>
              <span className="fdp-project-edit-card-label">Post Graduation Entry</span>
            </div>
          </div>
          <div className="fdp-project-edit-card-body">
            <div className="fdp-form-grid-2" style={{ marginBottom: 14 }}>
              <div className="fdp-form-field">
                <label className="fdp-form-label">Master's Degree</label>
                <FdpSelect
                  value={element.degree || ''}
                  onChange={(val) => handlePostGraduationChange(i, 'degree', val)}
                  options={postGraduationDegreeOptions}
                  placeholder="Select Master's degree"
                />
                {educationValidated && hasAnyPostGraduationData() && (!element.degree || element.degree === '') && (
                  <span style={{ fontSize: 11, color: '#e53e3e', marginTop: 3, display: 'block' }}>Please select a master's degree.</span>
                )}
              </div>
              <div className="fdp-form-field">
                <label className="fdp-form-label">University Name</label>
                <input className="fdp-input" type="text" value={element.university_name || ''} maxLength={50} onChange={(e) => { if (e.target.value.length <= 50) handlePostGraduationChange(i, 'university_name', e.target.value); }} />
                {educationValidated && hasAnyPostGraduationData() && (!element.university_name || element.university_name.trim() === '') && (
                  <span style={{ fontSize: 11, color: '#e53e3e', marginTop: 3, display: 'block' }}>Please provide a valid university name.</span>
                )}
              </div>
            </div>
            <div className="fdp-form-grid-2">
              <div className="fdp-form-field">
                <label className="fdp-form-label">Year of Graduation</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <FdpSelect
                    value={element.month || ''}
                    onChange={(val) => handlePostGraduationChange(i, 'month', val)}
                    options={getPostGraduationFromYearOptions().map((y) => ({ value: String(y), label: String(y) }))}
                    placeholder="From"
                  />
                  <FdpSelect
                    value={element.year || ''}
                    onChange={(val) => handlePostGraduationChange(i, 'year', val)}
                    options={[{ value: '0', label: 'Pursuing' }, ...(element.month ? getTillYearOptions(element.month).map((y) => ({ value: String(y), label: String(y) })) : [])]}
                    placeholder="Till"
                    disabled={!element.month}
                  />
                </div>
                {educationValidated && hasAnyPostGraduationData() && ((!element.month || element.month === '') || (!element.year || element.year === '')) && (
                  <span style={{ fontSize: 11, color: '#e53e3e', marginTop: 3, display: 'block' }}>Please select both from and till years.</span>
                )}
              </div>
              <div className="fdp-form-field">
                <label className="fdp-form-label">Education Type</label>
                <FdpSelect
                  value={element.education_type || ''}
                  onChange={(val) => handlePostGraduationChange(i, 'education_type', val)}
                  options={educationTypeOptions}
                  placeholder="Select Education Type"
                />
                {educationValidated && hasAnyPostGraduationData() && (!element.education_type || element.education_type === '') && (
                  <span style={{ fontSize: 11, color: '#e53e3e', marginTop: 3, display: 'block' }}>Please select an education type.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <VStack align="stretch" spacing={6}>
        <Card bg={cardBg} p={6}>
          <HStack spacing={2} mb={4}>
            <MdSchool size={24} color="brand.500" />
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              Graduation
            </Text>
          </HStack>

          {isEditMode ? (
            <VStack align="stretch" spacing={4}>
              {graduationList.map((element, i) => renderGraduationEntry(element, i))}
            </VStack>
          ) : (
            graduation.length > 0 ? (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor} color={textColor}>Degree</Th>
                      <Th borderColor={borderColor} color={textColor}>University</Th>
                      <Th borderColor={borderColor} color={textColor}>Education Type</Th>
                      <Th borderColor={borderColor} color={textColor}>Year</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {graduation.map((edu, index) => (
                      <Tr key={edu.id || index} _hover={{ bg: hoverBg }}>
                        <Td borderColor={borderColor} color={textColor}>
                          {graduationDegreeOptions.find(opt => opt.value === edu.degree)?.label || edu.degree || '--'}
                        </Td>
                        <Td borderColor={borderColor} color={textColor}>{edu.university_name || '--'}</Td>
                        <Td borderColor={borderColor} color={textColor}>
                          {educationTypeOptions.find(opt => opt.value === edu.education_type)?.label || edu.education_type || '--'}
                        </Td>
                        <Td borderColor={borderColor} color={textColor}>
                          {edu.year === '0' ? 'Pursuing' : (edu.month && edu.year ? `${edu.month} - ${edu.year}` : '--')}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text color="gray.500">No graduation data available.</Text>
            )
          )}
        </Card>

        <Card bg={cardBg} p={6}>
          <HStack spacing={2} mb={4}>
            <MdSchool size={24} color="brand.500" />
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              Post Graduate (Optional)
            </Text>
          </HStack>

          {isEditMode ? (
            <VStack align="stretch" spacing={4}>
              {postGraduationList.map((element, i) => renderPostGraduationEntry(element, i))}
            </VStack>
          ) : (
            postGraduation.length > 0 ? (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor} color={textColor}>Degree</Th>
                      <Th borderColor={borderColor} color={textColor}>University</Th>
                      <Th borderColor={borderColor} color={textColor}>Education Type</Th>
                      <Th borderColor={borderColor} color={textColor}>Year</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {postGraduation.map((edu, index) => (
                      <Tr key={edu.id || index} _hover={{ bg: hoverBg }}>
                        <Td borderColor={borderColor} color={textColor}>
                          {postGraduationDegreeOptions.find(opt => opt.value === edu.degree)?.label || edu.degree || '--'}
                        </Td>
                        <Td borderColor={borderColor} color={textColor}>{edu.university_name || '--'}</Td>
                        <Td borderColor={borderColor} color={textColor}>
                          {educationTypeOptions.find(opt => opt.value === edu.education_type)?.label || edu.education_type || '--'}
                        </Td>
                        <Td borderColor={borderColor} color={textColor}>
                          {edu.year === '0' ? 'Pursuing' : (edu.month && edu.year ? `${edu.month} - ${edu.year}` : '--')}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text color="gray.500">No post graduation data available.</Text>
            )
          )}
        </Card>

        {isEditMode && (
          <div className="fdp-edit-action-bar">
            <button className="fdp-save-btn" onClick={() => handleUpdate('Education')} disabled={isUpdating}>
              {isUpdating ? 'Saving…' : 'Save Education'}
            </button>
          </div>
        )}
      </VStack>
    );
  };

  if (detailVariant === 'page') {
    const PAGE_TABS = [
      { label: 'Overview', icon: <MdPerson className="fdp-tab-icon" /> },
      { label: 'Experience', icon: <MdWork className="fdp-tab-icon" /> },
      { label: 'Projects', icon: <MdFolder className="fdp-tab-icon" /> },
      { label: 'Certifications', icon: <MdCardMembership className="fdp-tab-icon" /> },
      { label: 'Education', icon: <MdSchool className="fdp-tab-icon" /> },
    ];

    const tabPanelContent = [
      renderPrimaryIntroduction(),
      renderProfessionalExperience(),
      renderProjects(),
      renderCertifications(),
      renderEducation(),
    ];

    return (
      <div>
        {/* Custom tab bar */}
        <div className="fdp-tab-bar" role="tablist">
          {PAGE_TABS.map((tab, i) => (
            <button
              key={tab.label}
              role="tab"
              aria-selected={tabIndex === i}
              className={`fdp-tab${tabIndex === i ? ' active' : ''}`}
              onClick={() => onTabChange(i)}
              type="button"
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab panel */}
        <div className="fdp-tab-panel" role="tabpanel">
          {tabPanelContent[tabIndex]}
        </div>
      </div>
    );
  }

  return (
    <Tabs
      index={tabIndex}
      onChange={onTabChange}
      colorScheme="brand"
      variant="enclosed"
      isLazy
      lazyBehavior="keepMounted"
    >
      <TabList
        overflowX="auto"
        flexWrap="wrap"
        w="100%"
        sx={{ overflow: 'hidden' }}
      >
        <Tab whiteSpace="nowrap">
          <HStack spacing={2}>
            <MdPerson />
            <Text>Primary Introduction</Text>
          </HStack>
        </Tab>
        <Tab>
          <HStack spacing={2}>
            <MdWork />
            <Text>Professional Experience</Text>
          </HStack>
        </Tab>
        <Tab>
          <HStack spacing={2}>
            <MdFolder />
            <Text>Projects</Text>
          </HStack>
        </Tab>
        <Tab>
          <HStack spacing={2}>
            <MdCardMembership />
            <Text>Certifications</Text>
          </HStack>
        </Tab>
        <Tab>
          <HStack spacing={2}>
            <MdSchool />
            <Text>Education</Text>
          </HStack>
        </Tab>
      </TabList>

      <TabPanels>
        <TabPanel px={0} pt={4}>{renderPrimaryIntroduction()}</TabPanel>
        <TabPanel px={0} pt={4}>{renderProfessionalExperience()}</TabPanel>
        <TabPanel px={0} pt={4}>{renderProjects()}</TabPanel>
        <TabPanel px={0} pt={4}>{renderCertifications()}</TabPanel>
        <TabPanel px={0} pt={4}>{renderEducation()}</TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default FreelancerList;


